import React from 'react';
import { Flex, Text } from '@adobe/react-spectrum';
import './changelog.css';
import { Helmet } from 'react-helmet';
import { ChangelogPane } from './ChangelogPane';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { useDispatch } from 'react-redux';
import { setAppRoute } from '../../data/redux/slices/appSlice';

export function Changelog() {
    const dispatch = useDispatch();

    return (
        <Flex direction="column">
            <Helmet>
                <title>Changelog </title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href="https://www.commandersalt.com/faq" />
                <meta name="description" content="Changelog - Recent updates" />
                <meta name="twitter:title" content="Commandersalt.com - Changelog" />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content="Commandersalt.com - Changelog" />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content="Frequently asked questions" />
            </Helmet>
            <BreadCrumbs />
            <div className="changelogPageWrap">
                <div className="changelogHero">
                    <span className="changelogHeroEyebrow">Release Log</span>
                    <h1 className="changelogHeroTitle">Changelog</h1>
                    <p className="changelogHeroSubtitle">
                        Every scoring tweak, new feature, and bug fix shipped since the site's inception. Most recent
                        releases are pinned open at the top — scroll further down to see the full history.
                    </p>
                    <div className="changelogHeroCallouts">
                        <span className="changelogHeroCallout">
                            <span className="changelogHeroCalloutLabel">Latest</span>
                            <span className="changelogHeroCalloutValue">2026.4059</span>
                        </span>
                        <span className="changelogHeroCallout">
                            <span className="changelogHeroCalloutLabel">Scope</span>
                            <span className="changelogHeroCalloutValue">Scoring / UI / Data</span>
                        </span>
                    </div>
                </div>
                <div className="changelogTimeline">
                    <ChangelogPane title="Platform Release: 2026.4059" isOpen={true}>
                        <span className={'deployDate'}>Deployed on: 05.29.2026</span>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            A second anchor-calibrated tuning pass, this time at the top and middle of the curve —
                            lifting genuine competitive closers that the win-condition axis was under-reading, and
                            capping wide-but-slow combo piles that read stronger than they actually play.
                            <p />
                            <ul>
                                <li>
                                    <b>Competitive infrastructure floors</b> — fully-tuned cEDH engines (maxed
                                    consistency and efficiency), prison / control-combo decks that win through
                                    stax-and-loop lines, and redundant-wincon shells backed by real interaction now get
                                    a structural floor instead of stalling in a dead zone just below their true band.
                                </li>
                                <li>
                                    <b>Enabler-pile ceilings</b> — a wide web of enablers with few true wincon combos
                                    and mediocre efficiency no longer reads as cEDH, and slow, sparse combo or
                                    high-synergy-without-a-combo shells are held in the casual band.
                                </li>
                                <li>
                                    <b>Smoother mid-curve gates</b> — disruptive low-infrastructure shells, focused
                                    synergy / tribal shells, mono-color single-line combo shells, and voltron decks all
                                    had their activation gates retuned against anchor decks.
                                </li>
                                <li>
                                    Combo value now reflects whether a deck can actually assemble its pieces, not just
                                    how many combos it happens to contain.
                                    <p />
                                    <ul>
                                        <li>
                                            <b>Findability-based combo scoring</b> — instead of a flat bonus for owning
                                            tutors, combo value is weighted by how reliably the deck can find the
                                            specific pieces of each line. A deck stuffed with tutors but clunky lines no
                                            longer scores like a streamlined one.
                                        </li>
                                        <li>
                                            <b>Fragile-wincon penalty</b> — high-power statline decks whose only win is
                                            a narrow combo they can&apos;t reliably assemble are pulled back down.
                                        </li>
                                        <li>
                                            <b>Infinite turns treated as an engine, not a win</b> — an infinite-turns
                                            loop only counts as a win condition when the deck also has a an actual clock
                                            to close with.
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            Transmute / transfigure tutors (&quot;search for a card with the same mana value&quot;) now
                            resolve to the correct same-cost targets instead of being treated as fetch-anything tutors.
                        </Text>
                        <Text>
                            <span className={'header'}>Deck Imports</span>
                            <p />
                            TopDecked imports of larger decks were intermittently failing during card lookup. Card
                            resolution is now batched, so those imports go through cleanly.
                        </Text>
                        <div className="changelogSectionDivider">
                            <span className="changelogSectionDividerLabel">UI Changes</span>
                        </div>
                        <Text>
                            <span className={'header'}>Upgrade Finder — card filters</span>
                            <p />
                            Stackable filters on the card itself, so a search like &quot;white draw under 3 mana that
                            isn&apos;t already in my deck&quot; is one query instead of eyeballing a list.
                            <p />
                            <ul>
                                <li>
                                    <b>Oracle text search</b> — exact-phrase match against rules text (e.g.{' '}
                                    <i>whenever you cast a noncreature spell</i>). For &quot;enters the
                                    battlefield&quot; effects use the structured card-action filter below — it catches
                                    every templating variant, not just the literal phrase.
                                </li>
                                <li>
                                    <b>Mana value range</b> — min and/or max MV (0–99) to keep suggestions on-curve.
                                </li>
                                <li>
                                    <b>Keywords</b> — flying, lifelink, deathtouch, ward, flash, double strike, and the
                                    rest, or type any keyword in directly.
                                </li>
                                <li>
                                    <b>Super types &amp; subtypes</b> — legendary / basic / snow, plus open-ended
                                    creature/permanent subtypes (elf, warrior, vehicle, …).
                                </li>
                                <li>
                                    <b>Produces mana</b> — filter by the colors a card can make; asking for green also
                                    surfaces rainbow producers (Birds of Paradise) automatically.
                                </li>
                                <li>
                                    <b>Multi-role search</b> — union several categories in one pass (Draw + Ramp +
                                    Tutors) instead of flipping between them one at a time.
                                </li>
                                <li>
                                    <b>Combo completion</b> — &quot;only cards that complete a combo already in my
                                    deck&quot; restricts results to the exact missing piece(s) for combos you&apos;re
                                    one card away from assembling.
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Upgrade Finder — effect, trigger &amp; result filters</span>
                            <p />
                            The deep cut: filter by the mechanical shape of a card&apos;s abilities, matched against the
                            same parsed effect data the scoring engine uses. This is how you find &quot;cards that draw
                            when an opponent does something&quot; rather than every card with the word &quot;draw&quot;
                            on it.
                            <p />
                            <ul>
                                <li>
                                    <b>Effect type</b> — triggered ability, activated ability, static, replacement,
                                    casting-cost, or the resulting effect. Pair with an <b>activation-cost range</b> to
                                    find, say, activated abilities you can fire for two or less.
                                </li>
                                <li>
                                    <b>Trigger condition</b> — who or what sets the ability off: your own actions, an
                                    opponent&apos;s, or a targeted player; the action that fires it (draw, cast,
                                    sacrifice, gain life, …); card events like a creature entering, dying, attacking, or
                                    being tapped for mana; and the zones the trigger reads (graveyard, exile, library,
                                    command, …).
                                </li>
                                <li>
                                    <b>Result</b> — the same dimensions for what the card <i>does</i> when it resolves:
                                    the acting player, the action (draw, mill, search, return from graveyard, make a
                                    token), and the zones the effect moves cards between.
                                </li>
                                <li>
                                    <b>Qualifier matching</b> — the precise one: match the subject of an effect by
                                    quantity, type, counter type, power/toughness, mana value, named card, and more — on
                                    either the trigger or result side. &quot;Draws two or more cards,&quot;
                                    &quot;searches for a creature,&quot; and &quot;adds +1/+1 counters&quot; are all
                                    expressible.
                                </li>
                                <li>
                                    <b>Match depth control</b> — bump how many matching lines each card reports, and the
                                    page size, when you want a denser drill-through.
                                </li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 2026.4030" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 05.23.2026</span>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            A big sweep through the power-level scorer to kill cliff jumps between adjacent decks and to
                            stop multi-wincon piles from masquerading as cEDH. Hard if/then anti-pattern gates have been
                            replaced with smooth activations, so a single metric crossing an integer boundary no longer
                            yanks a deck a full point up or down.
                            <p />
                            <ul>
                                <li>
                                    <b>Multi-wincon pile detection</b> — kitchen-sink finisher piles, isolated
                                    single-line wincons stacked together, and "many wincon combos, no tutors, no
                                    counters" shells now get properly capped instead of riding their combo count into
                                    the optimized band.
                                </li>
                                <li>
                                    <b>Anchor-calibrated structural floors</b> — compact cEDH infrastructure,
                                    permission-and-control value shells, voltron suit-up, landless combo lines, tribal
                                    combat decks, and high-synergy story engines now get a structural floor when their
                                    construction lines up, instead of being held down by missing checklist items.
                                </li>
                                <li>
                                    <b>Anchor-calibrated structural ceilings</b> — high-synergy precons with no payoff,
                                    accidental-combo precons, and tutored combo lines on a poor manabase get a
                                    structural ceiling that pulls them back into their actual band.
                                </li>
                                <li>
                                    <b>Smoother thresholds</b> — mass land destruction, group slug, pillow fort, discard
                                    control, multi-card combos, low-quality tutors, and multi-deficit fast-mana stacks
                                    all transition smoothly across their activation range, so two near-identical decks
                                    no longer end up a full point apart.
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Manabase scoring</span>
                            <p />
                            The manabase scorer had been telling the suggestion engine that swapping real untapped duals
                            for Evolving Wilds / Terramorphic Expanse / Fabled Passage was an upgrade. It is not. Four
                            scoring paths conspired to overrate slow basic-fetches and underrate non-basic duals — all
                            four are now corrected.
                            <p />
                            <ul>
                                <li>
                                    <b>Slow basic-fetches now take a tap penalty</b> — Evolving Wilds, Terramorphic
                                    Expanse, Fabled Passage, and similar cards fetch the basic tapped, so they get the
                                    same slow-fetch penalty as other tap lands. Prismatic Vista (untapped fetch) is
                                    correctly excluded.
                                </li>
                                <li>
                                    <b>Real untapped duals now count as basic-excess mitigators</b> — Underground River,
                                    Darkwater Catacombs, Gloomlake Verge and similar duals soften the too-many-basics
                                    penalty alongside basic-fetches instead of being ignored.
                                </li>
                                <li>
                                    <b>Premium fetch bonus scales with actual fetchable targets</b> — Polluted Delta in
                                    a deck with zero Island/Swamp non-basics no longer gets full credit; the per-color
                                    premium-fetch bonus saturates only once you have real targets to find.
                                </li>
                                <li>
                                    <b>Manabase stats no longer ignore card copies</b> — basics with 7+ copies were
                                    being counted as one when aggregating average land count, mana sources, fast-mana
                                    count, and the mana-value histogram. Manabase stats and avgCMC are now accurate.
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            A wide pass of category-detection bug fixes from forum reports and ground-truth audits.
                            Categories drive nearly every downstream score (power level, synergy, brackets, salt), so
                            each of these fixes ripples through deck assessment.
                            <p />
                            <ul>
                                <li>
                                    <b>Better wincon detection</b> — Scute Swarm and Field of the Dead are now
                                    recognized as wincons when their token engines actually close games.
                                </li>
                                <li>
                                    <b>Better board-wipe / interaction detection</b> — Singularity Rupture is now
                                    flagged as board wipe + interaction; Thraben Charm's graveyard-exile mode is no
                                    longer mistakenly tagged as a board wipe.
                                </li>
                                <li>
                                    <b>Better spot-removal detection</b> — Druid of Purification now picks up spot
                                    removal; Soulherder is no longer flagged as spot removal (a previous fix had
                                    over-applied the tag).
                                </li>
                                <li>
                                    <b>Better evasion detection</b> — Feign Death and Undying Malice are now categorized
                                    as evasion (protection effects), not just recursion.
                                </li>
                                <li>
                                    <b>Better stax detection</b> — Henrika Domnathi and Nowhere to Run no longer
                                    false-positive as stax from "each player" / generic text patterns.
                                </li>
                                <li>
                                    <b>Better cantrip / draw detection</b> — Communal Brewing (group draw, not single
                                    cantrip), Wakka Devoted Guardian, and a regex that was misclassifying assorted
                                    one-shot draw effects all fixed. Auntie Ool's commander synergy bonus for cantrip /
                                    draw now applies correctly.
                                </li>
                                <li>
                                    <b>Better recursion / topdeck / theft / groupslug detection</b> — Shadow of the
                                    Goblin no longer flagged as recursion; Lim-Dul's Vault now picks up topdeck
                                    manipulation; Dream Harvest now picks up theft; Tokka &amp; Rahzar (Terrible Twos)
                                    now picks up groupslug.
                                </li>
                                <li>
                                    <b>Better keyword parsing</b> — Stoic Strategist's "Prowl" cost is no longer
                                    mis-applied as the prowl ability keyword (a Scryfall data quirk).
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Deck import</span>
                            <p />
                            Two long-standing Archidekt importer bugs and one Hareruya copy of the same bug, plus better
                            handling for Cloudflare-protected imports.
                            <p />
                            <ul>
                                <li>
                                    <b>Maybeboard "Commander" tags ignored</b> — Archidekt decks where a maybeboard card
                                    still carried a leftover "Commander" category were producing phantom extra
                                    commanders (e.g. a Lorehold deck importing as three commanders). The importer now
                                    respects the sideboard / includedInDeck flag.
                                </li>
                                <li>
                                    <b>Duplicate-printing copies counted correctly</b> — decks with multiple printings
                                    of the same card (10x Island across different art printings, for example) were being
                                    imported with the wrong total because each printing entry was adding 1 instead of
                                    its actual quantity. Both Archidekt and Hareruya had the same bug; both are fixed.
                                </li>
                                <li>
                                    <b>Lutri unblocked in the 99</b> — running Lutri, the Spellchaser as a regular deck
                                    card no longer flags the deck as illegal. (Lutri remains illegal as a companion; the
                                    legality check is now properly scoped to the companion slot.)
                                </li>
                                <li>
                                    <b>Cleaner import errors</b> — Archidekt imports that get bounced by Cloudflare now
                                    surface a clear failure instead of a cryptic parse error.
                                </li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 2026.3700" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 05.16.2026</span>
                        <Text>
                            <span className={'header'}>Archetype-filtered deck composition stats</span>
                            <p />
                            The commander Deck Composition panel and Upgrade Finder both pick up an{' '}
                            <b>archetype filter</b> alongside the existing bracket / power-level picker. Narrow stats to
                            a major archetype (Midrange / Control / Combo) and optionally drill to a sub-archetype
                            (Combat, Tokens, Stax, Storm, etc.) — every Cards / Combos / Synergy / Manabase tab
                            re-aggregates against only the matching decks.
                            <p />
                            <ul>
                                <li>
                                    <b>Tuned Upgrade Finder picks up archetype too</b> — Staples, Synergy, Manabase,
                                    Archetype, and Combos modes all accept the same archetype filter, so suggestions can
                                    be biased toward "what Control players in this bracket actually run."
                                </li>
                                <li>
                                    <b>Low-sample badge</b> — when a (bin × archetype) cell has fewer than 30 decks, the
                                    panel surfaces a "Low sample" badge so noisy percentages don't get over-read.
                                </li>
                                <li>
                                    <b>Degraded-read banner</b> — if the stats store briefly returns degraded reads, a
                                    non-blocking banner appears instead of nuking the page; on-screen data stays intact
                                    while you retry.
                                </li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 2026.3677" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 04.22.2026</span>
                        <Text>
                            <span className={'header'}>Site-wide redesign</span>
                            <p />
                            Every page on the site has been refactored onto a shared "seared metal" dark theme — red
                            accents, gold highlights, panel hairlines, and tile-based metric rows applied consistently
                            across the deck details page, commander pages, meta, leaderboard, user profile, author
                            details, playtest, results listing, FAQ, changelog, and print dialog.
                            <p />
                            <ul>
                                <li>
                                    <b>Unified card info dialog</b> — clicking a card anywhere on the site (deck panes,
                                    commander stats, meta staples, salt breakdown) now opens the same modal with rules
                                    text, parsed effects, role assignments, the full category-scoring chain, and every
                                    Scryfall printing with per-print USD / foil / etched pricing
                                </li>
                                <li>
                                    <b>Deck page overhaul</b> — redesigned hero / assessment rail, Report Card, Flavor
                                    Profile, Nutrition Facts, and Field Report. The Practical Power Level meter is wider
                                    and uses a green → yellow → orange ramp across values 1–8, with a separate cEDH
                                    ember zone for 9–10 that only lights up when a deck actually scores there
                                </li>
                                <li>
                                    <b>Cards pane consolidation</b> — the old Categories and Deck List panes are gone;
                                    one Cards pane handles role chips, type filter, free-text search, and expandable
                                    detail rows. MDFC / transform back faces (e.g., Harnfel, Horn of Bounty) now appear
                                    next to their front face since each face contributes its own salt and category
                                    scoring
                                </li>
                                <li>
                                    <b>Deck export button</b> — copies the full decklist to clipboard in MTGO format
                                    (commander on top, alphabetised mainboard, double-faced cards collapsed into a
                                    single "Front // Back" line)
                                </li>
                                <li>
                                    <b>Manuel dialog</b> — the Field Report card on a deck page is now clickable and
                                    opens a dialog introducing Manuel, our Discord roast bot, with a Discord invite CTA
                                </li>
                                <li>
                                    <b>Meta page redesign</b> — replaces the old four-up table with tier bands (Tier
                                    1–4), partner-aware stacked commander art, meta-share bars, and a Staples tab with
                                    type chips, category chips, and free-text search. Mobile gets a tier picker so Tier
                                    4 isn't an endless scroll
                                </li>
                                <li>
                                    <b>Salt breakdown surface</b> — new SaltBreakdown panel with personality classifier,
                                    per-category tiles, Top Offenders, Stackable Offenders (cards appearing in 2+ salt
                                    categories), and a Swap Impact panel showing % salt removed by dropping the top N
                                    contributors. Also fixes an EDHRec salt miscalculation that was inflating Top
                                    Offender entries with phantom 100% pills
                                </li>
                                <li>
                                    <b>Animated salt-shaker footer icon</b> — replaces the old [?] info button. Shakes
                                    every 30 seconds (and on hover) and spawns a few grains of salt out of the spout
                                </li>
                                <li>
                                    Fixed a routing bug where clicking a commander tile from the Meta page after the
                                    initial load left the commander page blank
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Commander stats panel</span>
                            <p />
                            The commander details page is rebuilt around a new card-stats panel that surfaces everything
                            the index knows about how decks under that commander actually look. The page is now a
                            unified hero (commander art plus a power / bracket / salt / synergy stat grid) on top, a
                            compact deck list on the left, and the stats panel on the right.
                            <p />
                            <ul>
                                <li>
                                    <b>Bin picker</b> — slice every metric on the panel by Bracket (1–5) or Power Level
                                    band (1–3, 3–5, 5–7, 7–9, 9–10). The default bin auto-selects from the commander's
                                    bracket rating so you land on the most representative slice without clicking
                                </li>
                                <li>
                                    <b>Cards tab</b> — every card that has appeared in a deck for this commander, ranked
                                    by inclusion rate, with category-role chips, a chip browser for filtering by role,
                                    and a free-text search. Each row shows the card's mana cost, type, and the % of
                                    decks that run it
                                </li>
                                <li>
                                    <b>Combos tab</b> — the most-played combo lines for the commander with frequency
                                    counts and the cards involved. Click any piece to open the shared card info dialog
                                </li>
                                <li>
                                    <b>Synergy tab</b> — cards that consistently show up alongside the commander's
                                    high-synergy anchors, ranked by co-occurrence rate. This is the same data feeding
                                    the new Synergy upgrade source on the Upgrades panel
                                </li>
                                <li>
                                    <b>Manabase tab</b> — aggregated land breakdown for the commander's color identity:
                                    fast-mana lands, fixing lands, utility lands, and basic counts, with inclusion rates
                                    so you can see which lands are actually format-standard for the deck shape
                                </li>
                                <li>
                                    <b>Archetypes tab</b> — distribution of archetype classifications across all decks
                                    running the commander, so you can see whether the meta is leaning combo, midrange,
                                    aristocrats, etc., for that pilot
                                </li>
                                <li>
                                    All five tabs share the same row primitives, chart theme, role-color palette, and
                                    filter components — so charts, chips, and tooltips read consistently across tabs and
                                    match the new deck-page panes
                                </li>
                                <li>
                                    Clicking any card row anywhere on the panel opens the unified card info dialog
                                    (rules text, parsed effects, scoring chain, every Scryfall printing with pricing). A
                                    "Page" affordance on commander tiles in Meta opens the full commander page
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Player profile panel</span>
                            <p />
                            User profile pages get a new UserStatsPanel that turns your decklist history into an actual
                            player portrait. The hero leads with a MaRo-style psychographic classification, and the rest
                            of the panel is the supporting evidence.
                            <p />
                            <ul>
                                <li>
                                    <b>Player-type radar</b> — five-axis radar chart scoring you across Timmy / Tammy
                                    (the Experiencer), Johnny / Jenny (the Expressionist), Spike (the Competitor),
                                    Vorthos (the Flavor Lover), and Mel (the Mechanics Lover). Axes are kept in a fixed
                                    order so silhouettes are visually comparable across profiles
                                </li>
                                <li>
                                    <b>Primary / secondary classification</b> — your top two axes are called out
                                    explicitly, alongside subtype flavors (Power Gamer, Combo Player, Innovator, Tuner,
                                    Deck Artist, etc.) drawn from Mark Rosewater's "Timmy, Johnny, Spike Revisited" and
                                    "Vorthos and Mel" axis sub-divisions
                                </li>
                                <li>
                                    <b>Player-type info dialog</b> — explains what each axis and subtype means, with the
                                    cues we look for in a deck and explicit "undetectable" notes for subtypes that
                                    require data we don't track (e.g., game-log mulligan choices for Spike's Nuts &
                                    Bolts, art / alter preferences for Vorthos's Artist)
                                </li>
                                <li>
                                    <b>Evidence section</b> — concrete deck signals that drove your classification, so a
                                    Spike score has receipts (fast-mana density, tutor counts, staple interaction)
                                    rather than reading as a black-box label
                                </li>
                                <li>
                                    <b>Archetype mix</b> — your major and minor archetypes, your top archetypes by deck
                                    count, and an overall archetype-diversity score (one-trick pony vs. chameleon)
                                </li>
                                <li>
                                    <b>Color profile</b> — every color combination you've registered as a deck, ranked
                                    by frequency, plus per-color totals so it's obvious whether you're a mono-black
                                    loyalist or a five-color tourist
                                </li>
                                <li>
                                    <b>Signature cards & top commanders</b> — the cards and commanders that show up most
                                    distinctively across your decks (not just the most-played, but the ones that most
                                    define your style relative to the index)
                                </li>
                                <li>
                                    <b>Top cards & top combos</b> — the cards you reach for most often and the combo
                                    lines you keep building around
                                </li>
                                <li>
                                    <b>Category role breakdown</b> — average ramp / interaction / draw / wincon counts
                                    per deck, so you can see whether you're systematically light on a category
                                </li>
                                <li>
                                    <b>Voice and status states</b> — copy adapts to whether you're viewing your own
                                    profile or someone else's, and the panel has explicit states for "data still
                                    computing", "not enough decks yet", and "stats service offline" instead of rendering
                                    empty charts
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>New: Expanded upgrade suggestions</span>
                            <p />
                            The Upgrades panel can now source suggestions from several new angles, powered by the
                            commander card stats pipeline:
                            <p />
                            <ul>
                                <li>
                                    <b>Synergy</b> — cards that repeatedly show up alongside high-synergy anchors in
                                    other decks for this commander
                                </li>
                                <li>
                                    <b>Archetype</b> — cards top decks in your archetype include that you don't
                                    currently run
                                </li>
                                <li>
                                    <b>Combos</b> — combo pieces that fit your commander and colors, with overlap
                                    against your existing combo lines
                                </li>
                                <li>
                                    <b>Manabase</b> — land suggestions specific to your color identity and utility-land
                                    gaps
                                </li>
                                <li>
                                    <b>Staples</b> — format staples the commander's meta runs that are missing from your
                                    list
                                </li>
                                <li>
                                    <b>Category role</b> — targeted category gaps (ramp, interaction, card draw) based
                                    on what similar decks run
                                </li>
                            </ul>
                            The default "card category score" source is unchanged — new sources are opt-in.
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            Several anchor-driven scoring refinements for decks the model was misreading.
                            <p />
                            <ul>
                                <li>
                                    Budget control shells with real counterspell density (3+ counterspells, no fast
                                    mana, no combos) now get a dedicated boost — previously they were under-rated
                                    because their interaction ratio normalized against cEDH baselines
                                </li>
                                <li>
                                    Pure-control decks with meaningful counterspell density are no longer penalized as
                                    "can't close games" — the penalty scales down proportional to counter depth, since
                                    counter-heavy control wins through inevitability
                                </li>
                                <li>
                                    High-synergy intentional combo builds (e.g., focused lifegain/sacrifice shells with
                                    a built-in combo finish) are no longer penalized as "janky combo" or flagged as
                                    combo-inconsistent when they don't run tutors — the synergy itself is the redundancy
                                </li>
                                <li>
                                    Precon detection now considers card quality as a gate — custom deliberate builds
                                    with high-quality cards are no longer misclassified as upgraded precons that
                                    accidentally tripped into a combo line
                                </li>
                                <li>
                                    Refined budget cEDH combo scoring so low-cost competitive shells land closer to
                                    their true power bracket (merged from main)
                                </li>
                                <li>
                                    Fixed four anchor-point power level violations and added explanation fields so tools
                                    like Manuel can surface why a given bias was applied (merged from main)
                                </li>
                                <li>Salt grading curve tweaked to better spread the middle of the distribution</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Commander legality</span>
                            <p />
                            Added full detection of the five sanctioned two-commander mechanics. Decks with invalid
                            commander pairings are now flagged and excluded from aggregation data so they don't skew
                            commander-level stats.
                            <p />
                            <ul>
                                <li>Partner (generic) and Partner with &lt;named&gt;</li>
                                <li>Friends forever</li>
                                <li>Doctor's companion with Time Lord Doctor</li>
                                <li>Choose a Background with legendary creature</li>
                                <li>
                                    Runs off Scryfall keyword data directly, so new sets with partner-style mechanics
                                    are supported automatically
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card context data</span>
                            <p />
                            A large batch of parser improvements. Context data powers synergy, category detection, and
                            the playtester — so these improvements cascade into more accurate scoring across the site.
                            <p />
                            <ul>
                                <li>
                                    Compound trigger detection — abilities phrased as "when X enters and whenever Y
                                    attacks" now properly tracked as two independent triggers sharing an effect
                                </li>
                                <li>
                                    New "or-statement" flags on action lists so downstream consumers (playtester,
                                    synergy matcher) can distinguish "tap or untap" from "tap and untap"
                                </li>
                                <li>
                                    Better library vs. hand peek disambiguation — "look at the top N of your library"
                                    and "look at target player's hand" are now tracked as distinct actions
                                </li>
                                <li>
                                    Sylvan Library-style "choose cards drawn this turn for life" tracked as its own
                                    action so the playtester stops firing the generic choose handler
                                </li>
                                <li>
                                    Attach actions (equipment / aura attachment) now properly detected in both
                                    conditions and results
                                </li>
                                <li>
                                    Cascade / discover keyword grants — cards like Zhulodok, Imoti, and Wild-Magic
                                    Sorcerer that grant cascade to other spells now produce proper game-state-modifier
                                    records
                                </li>
                                <li>
                                    Modular (+1/+1 counter ETB) correctly typed as a replacement effect with counter
                                    synergy, not a sacrifice-on-death trigger
                                </li>
                                <li>
                                    "Creatures you control have ..." and similar plural-type static grants now
                                    recognized as affecting the whole type the controller owns, not just self
                                </li>
                                <li>
                                    Variable power/toughness tokens (e.g., "power and toughness are each equal to X")
                                    now captured with */* P/T instead of being dropped
                                </li>
                                <li>
                                    Counter quantity parsing for "with N +1/+1 counters" and "put N charge counters" now
                                    captured alongside existing quantity detection
                                </li>
                                <li>
                                    Token-creation lines no longer mistakenly tagged with game-state modifiers — those
                                    describe the token's own abilities, not the creator's static effect
                                </li>
                                <li>
                                    Token parser fixes for variable P/T, color extraction, and qualifier parsing (merged
                                    from main)
                                </li>
                                <li>
                                    Stack-retarget effects ("choose new targets") correctly suppressed as un-modelable
                                    no-ops so the playtester doesn't fire spurious actions
                                </li>
                                <li>
                                    Keyword-granted reminder text (e.g., modular's "enters with N counters") now
                                    back-fills the structured flags that the raw reminder strips
                                </li>
                                <li>
                                    "Noncommander spell" qualifier corrected to "nonland spell" for several effect
                                    parsers that were matching too narrowly
                                </li>
                                <li>
                                    Resolved a CardContext parsing crash that was failing some cards silently during
                                    ingest
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            Synergy accuracy improves alongside the context-parser improvements above. Modular,
                            cascade-grant, attach, and plural static-grant patterns now participate correctly in synergy
                            matching where they previously went undetected.
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 2026.3123" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 04.02.2026</span>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            <ul>
                                <li>
                                    Score for lock combos like Stasis + Unstoppable Plan reduced significantly
                                    <ul>
                                        <li>
                                            This was artificially inflating decks that were heavy on stax and locks but
                                            light on actual ways to win the game
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 2026.3025" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 03.28.2026</span>
                        <Text>
                            <span className={'header'}>Card context data</span>
                            <p />
                            Improved card context parsing for damage attribution, self-sacrifice detection, and action
                            scope accuracy
                            <p />
                            <ul>
                                <li>
                                    Better damage action detection — creature-dependency triggers no longer
                                    misattributed as self-damage
                                </li>
                                <li>Improved self-sacrifice detection for fetchlands and similar effects</li>
                                <li>
                                    Better attack/block action parsing, excluding false positives from "tapped and
                                    attacking" clauses
                                </li>
                                <li>Added ordinal spell trigger parsing (e.g., "second spell each turn")</li>
                                <li>Improved quantity and qualifier parsing for draw, put, and "up to N" effects</li>
                                <li>Better token color detection for numbered token creation patterns</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            Improved synergy matching accuracy with better type filtering and reduced false positives
                            for group slug and generic cast effects
                            <p />
                            <ul>
                                <li>
                                    Improved noncreature "you may cast" synergy matching — no longer filtered as generic
                                </li>
                                <li>
                                    Better group slug synergy detection for cards with narrow type qualifiers (e.g.,
                                    nonbasic lands)
                                </li>
                                <li>
                                    Token-creating keyword triggers now properly recognized as ETB sources for synergy
                                    matching
                                </li>
                                <li>Improved "nonbasic" type matching — now correctly requires land type</li>
                                <li>Better mana value qualifier handling for spell-cast triggers</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            Multiple scoring adjustments improving accuracy for budget combo decks, stax builds, and
                            decks with interaction/efficiency imbalances
                            <p />
                            <ul>
                                <li>
                                    Improved precon detection — intentional budget combo decks no longer misidentified
                                    as precons
                                </li>
                                <li>
                                    Better cEDH optimization gating — borderline decks no longer receive unearned
                                    competitive bonuses
                                </li>
                                <li>
                                    Improved stax scoring — single-stax incidental inclusions no longer over-boosted
                                </li>
                                <li>Better manabase pullback for extremely poor land bases</li>
                                <li>Improved fragile combo capping — non-cEDH decks properly capped below 9.0</li>
                                <li>Commander cost reduction strategies now eligible for manabase bonuses</li>
                                <li>Removed hard cap on cantrip bonuses that was double-penalizing draw engines</li>
                                <li>Improved tutor quality gating for cEDH stax detection</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            <ul>
                                <li>
                                    Simplified "realistic" (eg non-WotC spec) bracket 4 assignment — power level 7+ now
                                    consistently maps to bracket 4
                                </li>
                                <li>Improved mass land denial combo detection using structured result data</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Combos</span>
                            <p />
                            <ul>
                                <li>Better combo categorization using structured result data as fallback</li>
                                <li>Improved lock and opponent-restriction pattern matching</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            <ul>
                                <li>Mox Opal now properly recognized as mana-positive</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Salt</span>
                            <p />
                            <ul>
                                <li>Fixed salt score total not including lock and infinite combo points</li>
                                <li>Increased lock combo salt scoring to better reflect play experience</li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 2026.2883" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 03.19.2026</span>
                        <Text>
                            <span className={'header'}>Card context data</span>
                            <p />
                            Major upgrade to the card context engine. Zones now support multi-zone tracking for cards
                            that interact with multiple game zones simultaneously. Added game state modifier detection
                            for enters-tapped tracking, token enrichment, and ability-granting effects.
                            <p />
                            <ul>
                                <li>
                                    Improved keyword ability detection for 30+ keywords with synergy data and activation
                                    flags
                                </li>
                                <li>Better qualifier parsing for scope, phase triggers, and pronoun back-references</li>
                                <li>Improved zone detection for search-library and cycling effects</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            Significant accuracy improvements across multiple card categories with better pattern
                            matching and fewer false positives.
                            <p />
                            <ul>
                                <li>
                                    Improved land tutor detection (Expedition Map, Sylvan Scrying now properly
                                    categorized as consistency)
                                </li>
                                <li>
                                    Better life loss multiplier detection (Bloodletter of Aclazotz, Archfiend of
                                    Despair)
                                </li>
                                <li>Improved stax detection for untap restriction effects (Storage Matrix)</li>
                                <li>Better burn detection for conditional damage effects</li>
                                <li>Improved cantrip scoring for repeatable self-cast triggers vs one-time ETB draw</li>
                                <li>
                                    Fixed false positive removal detection for self-targeting effects (Endless Sands)
                                </li>
                                <li>
                                    Reduced false positive lands-matter classification for incidental land references
                                </li>
                                <li>Improved cost reduction detection for affinity-granting abilities</li>
                                <li>Better ramp detection for creatures-become-lands effects (Ashaya)</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            Scoring adjustments addressing anchor point violations across multiple decks, with improved
                            accuracy for mono and dual-color manabases.
                            <p />
                            <ul>
                                <li>
                                    Manabase boost now scales with color count — mono/dual-color decks no longer
                                    over-rewarded for inherently easy mana
                                </li>
                                <li>Improved focused casual deck detection thresholds</li>
                                <li>10 targeted scoring adjustments fixing anchor point violations</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Manabase</span>
                            <p />
                            Overhauled manabase quality scoring for more accurate land evaluation.
                            <p />
                            <ul>
                                <li>
                                    Surveil lands and triomes no longer penalized as tap lands — their utility
                                    compensates for tempo cost
                                </li>
                                <li>
                                    MDFC back-face lands evaluated separately — entering tapped is a choice, not a
                                    penalty
                                </li>
                                <li>
                                    Utility lands no longer penalized for entering tapped when included for their
                                    effects
                                </li>
                                <li>
                                    Quality scoring restructured: standard bonuses cap at 100%, only original duals and
                                    fast mana push above
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            <ul>
                                <li>Improved CMC calculation for X-cost spells using realistic minimum X values</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Salt</span>
                            <p />
                            <ul>
                                <li>Land denial effects such as Blood Moon now properly contribute towards MLD</li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 2026.2523" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 03.13.2026</span>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            Expanded keyword synergy data across 30+ keywords including combat triggers (exalted, battle
                            cry, myriad, melee, bushido), ETB effects (fabricate, exploit, champion, vanishing), and
                            discard mechanics (cycling, landcycling, mountaincycling). Improved synergy text clarity by
                            omitting irrelevant ability descriptions for type-only matches.
                            <p />
                            <ul>
                                <li>
                                    Improved affinity synergy detection for non-artifact spells with generic mana costs
                                </li>
                                <li>
                                    Better token copy synergy detection (e.g., Mishra creating copies of noncreature
                                    artifacts)
                                </li>
                                <li>Cleaner synergy descriptions for expend, trigger doublers, and static effects</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            Improved detection accuracy across multiple categories.
                            <p />
                            <ul>
                                <li>
                                    Generic land tutors (Expedition Map, Sylvan Scrying) now properly categorized as
                                    consistency
                                </li>
                                <li>Better stax detection for untap-denial effects (Storage Matrix)</li>
                                <li>Improved spot removal detection, excluding self-targeting flicker effects</li>
                                <li>Better burn detection for optional damage/life-loss effects</li>
                                <li>Improved cost reduction detection for affinity-granting abilities</li>
                                <li>New ramp detection for creatures-become-lands effects (Ashaya)</li>
                                <li>Life loss doubling now detected as a multiplier (Bloodletter of Aclazotz)</li>
                                <li>Buyback no longer incorrectly triggers cantrip detection</li>
                                <li>
                                    Better creature cantrip scoring for repeatable self-cast triggers (Champions of the
                                    Perfect)
                                </li>
                                <li>
                                    Reduced false positives for lands-matter archetype from incidental land references
                                </li>
                                <li>Land denial effects (Blood Moon) now properly tagged in salt categories</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            Scoring accuracy improvements across multiple deck archetypes.
                            <p />
                            <ul>
                                <li>New voltron strategy boost for focused voltron builds with quality cards</li>
                                <li>Improved behold keyword scoring in tribal decks with many valid targets</li>
                                <li>
                                    Better grants-ability scoring for anthem/combat effects that scale with creature
                                    count
                                </li>
                                <li>
                                    Refined slow-competitive penalty with separate paths for synergy-rich and
                                    combo-control builds
                                </li>
                                <li>Better precon detection with purity-based compression adjustments</li>
                                <li>Tuned disruption floor and stax parity-breaking for fast-mana scaling</li>
                                <li>Improved control-combo boost gating to exclude budget builds</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card context data</span>
                            <p />
                            Significant improvements to the card context parser.
                            <p />
                            <ul>
                                <li>Added grants-ability detection for cards that give keywords to your creatures</li>
                                <li>
                                    Added structured phase trigger data (upkeep, end step, combat) with player scope
                                </li>
                                <li>Improved affinity detection for granted affinity abilities</li>
                                <li>Better zone detection for library search and top-of-library patterns</li>
                                <li>Improved keyword qualifier merging for parameterized keywords</li>
                                <li>Better cycling zone tracking (hand to graveyard)</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            <ul>
                                <li>Improved X-cost spell evaluation using per-pip minimum mana values</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Manabase</span>
                            <p />
                            <ul>
                                <li>Tapland utility bonus reduced to better reflect tempo cost of entering tapped</li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 2026.2445" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 02.27.2026</span>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            Major overhaul of the synergy detection engine. Added support for 25+ keyword synergies,
                            zone-based synergy detection across all MTG game zones, and a comprehensive keyword effects
                            registry with official rules text for 200+ keywords.
                            <p />
                            New keyword synergy detection:
                            <ul>
                                <li>Graveyard keywords: dredge, flashback, retrace, unearth, persist</li>
                                <li>
                                    Spellcasting keywords: cascade, conspire, improvise, affinity, convoke, magecraft
                                </li>
                                <li>
                                    Battlefield keywords: landfall, proliferate, fabricate, ascend, forage, manifest
                                    dread
                                </li>
                                <li>
                                    Other keywords: flying, surveil, lifelink, investigate, mill, discover, expend,
                                    valiant
                                </li>
                            </ul>
                            <p />
                            New zone-based synergy detection:
                            <ul>
                                <li>Library: top-of-library manipulation, mill, and reveal-from-library effects</li>
                                <li>Hand: draw triggers, discard triggers, and hand-size-matters effects</li>
                                <li>Battlefield: ETB/LTB triggers, tap/untap triggers</li>
                                <li>
                                    Graveyard: enters-graveyard triggers, cast-from-graveyard, reanimate,
                                    leaves-graveyard
                                </li>
                                <li>Stack: spell cast triggers and copy spell effects</li>
                                <li>Exile: exile-for-value (impulse draw), exile-as-resource, permanent exile</li>
                                <li>Command zone: commander-matters synergies</li>
                            </ul>
                            <p />
                            Additional improvements:
                            <ul>
                                <li>Improved detection for sacrifice, commit-a-crime, and spellslinger triggers</li>
                                <li>Better handling of removal spells with opponent compensation effects</li>
                                <li>
                                    Improved landfall synergy detection for ramp spells (Cultivate, Kodama's Reach,
                                    etc.)
                                </li>
                                <li>Performance improvements: reduced memory allocations and pre-compiled patterns</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            Improved accuracy across 20+ card categorization rules, reducing false positives and filling
                            detection gaps.
                            <p />
                            <ul>
                                <li>
                                    Improved win condition detection (token wincons, planeswalker wincons,
                                    stompy/voltron)
                                </li>
                                <li>Better burn detection (excludes damage reduction and creature-only effects)</li>
                                <li>Improved ramp detection (mana-producing artifacts, color identity awareness)</li>
                                <li>Better spot removal and control detection</li>
                                <li>Improved tax effect detection (pay-or-sacrifice effects)</li>
                                <li>Better evasion and stun counter categorization</li>
                                <li>Improved discard detection for triggered discard effects</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            Comprehensive scoring recalibration validated against 56 anchor point reference decks.
                            <p />
                            <ul>
                                <li>
                                    Replaced hard scoring thresholds with smooth S-curve transitions for more natural
                                    ratings
                                </li>
                                <li>
                                    Improved combo assessment: overlapping combo permutations no longer inflate scores
                                </li>
                                <li>Better detection of decks with many enabler combos but no win conditions</li>
                                <li>Improved scoring for disruptive synergy strategies (stax + high synergy decks)</li>
                                <li>Better differentiation between cEDH, high power, and casual combo decks</li>
                                <li>Improved control deck detection to prevent incorrect penalty application</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Combos</span>
                            <p />
                            New deterministic combo detection engine (beta) with 24 combo type evaluators. This is a{' '}
                            <b>
                                <i>closed beta</i>
                            </b>
                            , and is still very buggy :) If you would like it enabled for your user id, please ask in
                            the Discord!
                            <p />
                            <ul>
                                <li>Detects combos using card mechanics instead of name-based matching</li>
                                <li>
                                    Supports infinite mana, tokens, damage, lifegain, lifeloss, mill, draw, turns,
                                    combat, and more
                                </li>
                                <li>Improved infinite combat detection (Kiki-Jiki patterns, extra combat combos)</li>
                                <li>Better distinction between lifeloss and damage combo types</li>
                                <li>Reduced false positives for combat combo detection</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card context data</span>
                            <p />
                            The card context parser underwent a major overhaul to better understand card mechanics and
                            enable more accurate synergy and category detection.
                            <p />
                            <ul>
                                <li>New keyword effects registry with official WotC rules text for 200+ keywords</li>
                                <li>
                                    Synthetic context lines: keyword rules text is now expanded into structured context
                                    data, allowing category processors to automatically detect what keywords do
                                </li>
                                <li>
                                    Dynamic keyword detection from oracle text to fill gaps in Scryfall keyword data
                                </li>
                                <li>
                                    Improved ability classification: better distinction between triggered abilities,
                                    static abilities, and replacement effects
                                </li>
                                <li>
                                    Improved trigger-doubling detection (Panharmonicon, Yarok, Delney) now correctly
                                    classified as static abilities with condition/result nesting
                                </li>
                                <li>Better "as enters the battlefield" replacement effect handling</li>
                                <li>Improved compound trigger parsing ("dies or leaves the battlefield")</li>
                                <li>Better type qualifier parsing for multi-word type expressions</li>
                                <li>Improved back-reference handling for "this way" and "that" patterns</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Archetypes</span>
                            <p />
                            <ul>
                                <li>Improved planeswalker-heavy deck classification (superfriends detection)</li>
                                <li>
                                    New control deck archetype detection: reactive control decks (high counterspells +
                                    removal) are now properly distinguished from stax decks
                                </li>
                                <li>
                                    Improved poison archetype detection: proliferate cards no longer falsely trigger
                                    poison classification without actual poison/infect/toxic cards
                                </li>
                            </ul>
                        </Text>
                    </ChangelogPane>

                    <ChangelogPane title="Platform Release: 2026.972" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 01.20.2026</span>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            <ul>
                                <li>
                                    Removed "vibe check" from WotC spec bracket analysis. This was overcomplicating
                                    things, as the "realistic" bracket rating does essentially the same thing
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Salt</span>
                            <p />
                            <ul>
                                <li>Added salt values for cards based on their price</li>
                                <li>Fixed issue where cEDH staples were missing from the UI in the salt pane</li>
                            </ul>
                        </Text>
                    </ChangelogPane>

                    <ChangelogPane title="Platform Release: 2026.482" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 01.03.2026</span>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            Improved accuracy for decks with non-winning combos
                            <p />
                            <ul>
                                <li>Decks with many combos but no win conditions now score more accurately</li>
                                <li>Better differentiation between competitive and casual combo decks</li>
                                <li>Improved scoring for decks lacking fast mana</li>
                            </ul>
                        </Text>
                    </ChangelogPane>

                    <ChangelogPane title="Platform Release: 2026.479" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 01.01.2026</span>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            <ul>
                                <li>Improved exile-to-cast synergy detection (cascade, discover, impulse draw)</li>
                                <li>Better noncombat damage synergy recognition</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            <ul>
                                <li>Improved recursion detection (graveyard retrace/flashback effects)</li>
                                <li>Better board wipe recognition for modal spells</li>
                                <li>Improved land destruction detection (basic land type targeting)</li>
                                <li>Better taxes detection (opponent may-pay effects)</li>
                                <li>Improved anthem detection for toughness-matters decks</li>
                                <li>Better burn detection (excludes creature-only damage)</li>
                                <li>Improved group hug detection (active player mana effects)</li>
                                <li>Better overrun detection (trample/infect requirements)</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            <ul>
                                <li>Added stax parity-breaking detection for commanders</li>
                                <li>Improved manabase quality scoring curve</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            <ul>
                                <li>Land denial cards now properly count toward bracket determination</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card context data</span>
                            <p />
                            <ul>
                                <li>Better named card parsing in card text</li>
                                <li>
                                    Improved qualifier detection (type, color, quantity, name, etc) in complex oracle
                                    text
                                </li>
                            </ul>
                        </Text>
                    </ChangelogPane>

                    <ChangelogPane title="Platform Release: 2025.4757" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 12.15.2025</span>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            <ul>
                                <li>Improved token doubler synergies (Parallel Lives, Doubling Season)</li>
                                <li>Better counter doubler detection</li>
                                <li>Improved exile trigger synergies (Ketramose)</li>
                                <li>Improved enter/leave graveyard trigger synergies (Syr Konrad)</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            <ul>
                                <li>Improved counterspell detection</li>
                                <li>Better boardwipe categorization including modal spells (Farewell)</li>
                                <li>Added Avatar mechanics support (airbend, waterbend, earthbend, firebend)</li>
                                <li>Improved burn detection and scoring</li>
                                <li>Better topdeck categorization including miracle cards</li>
                                <li>Improved token detection (amass, investigate, incubate, fabricate)</li>
                                <li>Better stompy detection for X-cost creatures</li>
                                <li>Improved stax detection for counter-halving effects</li>
                                <li>Better cost reduction detection</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            <ul>
                                <li>
                                    More refinement of deck characteristics which inform the final power level grading
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Combos</span>
                            <p />
                            <ul>
                                <li>Improved combo search performance</li>
                                <li>Better wincon combo detection</li>
                                <li>Infinite exile combos now properly scored as wincons</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Archetypes</span>
                            <p />
                            <ul>
                                <li>Added Blink and Exalted archetypes</li>
                                <li>Improved Storm archetype detection</li>
                                <li>Better Voltron detection for scaling commanders</li>
                                <li>Fixed Stax deck labeling for decks with significant stax presence</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card context data</span>
                            <p />
                            <ul>
                                <li>Better zone detection for exile effects</li>
                                <li>Improved replacement effect parsing</li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 10032025.0.4340" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 11.05.2025</span>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            <ul>
                                <li>Improved token categorization (treasures, copies, efficient removal)</li>
                                <li>Better voltron detection (equipment, auras, power buffs)</li>
                                <li>Improved MLD detection (stax effects that don't tap lands)</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            <ul>
                                <li>Better scoring for combo-focused decks</li>
                                <li>Improved detection of high-quality decks with strong tutor packages</li>
                                <li>Better handling of control and interaction-heavy strategies</li>
                                <li>More accurate low-power deck identification</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Combos</span>
                            <p />
                            <ul>
                                <li>Infinite tokens with haste now correctly marked as win conditions</li>
                            </ul>
                        </Text>
                    </ChangelogPane>

                    <ChangelogPane title="Platform Release: 10032025.0.4165" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 11.04.2025</span>
                        <p />
                        <Text>
                            <span className={'header'}>Upgrades screen and card scoring</span>
                            <p />
                            One of the most criticized parts of the site has been individual card scoring, and for good
                            reason. There were many, many gaps and irregularities in the scoring algorithm.
                            <p />
                            The most glaring example of this was the Upgrades panel - it was pretty much unusable
                            because there was so many garbage cards being scored too highly.
                            <p />
                            To try and close some of these gaps, I have completely overhauled the card scoring system
                            <ul>
                                <li>
                                    I analyzed thousands of verified cEDH lists from edhtop16.com (that actually placed
                                    well), and then collated a breakdown of cEDH staple cards per card category
                                </li>
                                <li>
                                    I then used AI tools to detect patterns in those cards which would lead us poor
                                    humans to evaluate them as being "objectively good"
                                </li>
                                <li>
                                    The individual card category scoring systems were then rewritten to reflect these
                                    patterns, evaluating cards across 6 core dimensions
                                </li>
                                <ul>
                                    <li>
                                        <b>Mana efficiency</b> - Value generated versus mana cost (most important
                                        factor)
                                    </li>
                                    <li>
                                        <b>Flexibility and qualifiers</b> - Fewer restrictions means exponentially
                                        higher value
                                    </li>
                                    <li>
                                        <b>Speed and timing</b> - Instant-speed interaction is worth 25% more than
                                        sorcery-speed
                                    </li>
                                    <li>
                                        <b>Repeatability</b> - Opponent-triggered and permanent-based effects compound
                                        value over time
                                    </li>
                                    <li>
                                        <b>Free and alternate costs</b> - Free-cast overrides printed CMC penalties
                                        entirely
                                    </li>
                                    <li>
                                        <b>CMC baseline</b> - Exponential penalty for higher costs (73% of top cEDH
                                        cards are CMC 0-1)
                                    </li>
                                </ul>
                            </ul>
                            <p />
                            With all of this, the Upgrades screen should finally be actually useful. That being said, it
                            will take a bit for the full cache of cards to be updated, so do check back once in a while
                            ;)
                            <p />
                            Another important component of all of this is of course that power level evaluation also
                            improved because individual card scoring improved.
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            A large focus of this update was around better recognition of cEDH-optimized decks;
                            specifically the boundary between power level 8.99x (top of high power range), and 9.0x (the
                            bottom of cEDH power range).
                            <p />
                            The algorithm looks at several factors to distinguish high-power from fringe cEDH:
                            <ul>
                                <li>
                                    <b>Fast mana density</b> - Amount and quality of mana acceleration
                                </li>
                                <li>
                                    <b>Tutor package</b> - Number and efficiency of tutors
                                </li>
                                <li>
                                    <b>Interaction suite</b> - Stack interaction and board control
                                </li>
                                <li>
                                    <b>Win condition speed</b> - How quickly the deck can close out games
                                </li>
                                <li>
                                    <b>Manabase quality</b> - Optimization and speed of the landbase
                                </li>
                                <li>
                                    <b>Overall consistency</b> - Draw engines, recursion, and redundancy
                                </li>
                            </ul>
                            <p />
                            Other updates in this release for power levels:
                            <ul>
                                <li>Improved scoring for synergy-based strategies (tokens, combat, aristocrats)</li>
                                <li>Raised tier caps to allow optimized builds to reach competitive scores</li>
                                <li>Fixed efficiency rating boundary for high-powered decks</li>
                                <li>Improved commander scoring to reduce tier penalties on optimized lists</li>
                                <li>Better manabase quality impact on overall ratings</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            <ul>
                                <li>Improved bracket 4 determination logic</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Combos</span>
                            <p />
                            <ul>
                                <li>Added craft/transform cost calculation to complexity analysis</li>
                                <li>Better detection of mana requirements for combo execution</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            Detection fixes and improvements for the following categories
                            <ul>
                                <li>Draw</li>
                                <li>Burn</li>
                                <li>Top deck</li>
                                <li>Tutors</li>
                                <li>Recursion</li>
                                <li>Cost reduction</li>
                                <li>Spot removal</li>
                                <li>Evasion</li>
                                <li>Stax</li>
                                <li>Cheat</li>
                                <li>Overrun</li>
                            </ul>
                            <p />
                            Additionally there is a new category added -{' '}
                            <span style={{ color: '#05eaff' }}>
                                <b>Multipliers</b>
                            </span>
                            <ul>
                                <li>
                                    This covers anything that multiplies the value of effects. Doubling Season, Delney,
                                    Elesh Norn, etc
                                </li>
                                <li>This counts towards consistency in power level calculations</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Manabase</span>
                            <p />
                            <ul>
                                <li>Fixed mana castability probability calculations for multi-colored cards</li>
                                <li>Better handling of color requirements</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card context data</span>
                            <p />
                            <ul>
                                <li>Improved English-only, paper-legal card filtering from Scryfall</li>
                                <li>Better transform/craft card handling</li>
                            </ul>
                        </Text>
                    </ChangelogPane>

                    <ChangelogPane title="Platform Release: 10032025.0.3031" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 10.04.2025</span>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            <ul>
                                <li>
                                    Improved win condition detection for mass reanimation effects and storm strategies
                                </li>
                                <li>
                                    Better distinction between spot removal and board wipes for targeted debuff effects
                                </li>
                                <li>Refined clone detection to match more copy effect patterns</li>
                                <li>Fixed counterspell category to properly include spell-stealing effects</li>
                                <li>
                                    Improved control card detection for Pacifism-type effects and player-targeted
                                    triggers
                                </li>
                                <li>
                                    Better spot removal scoring for rare creature type restrictions and self-damage
                                    effects
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            <ul>
                                <li>Reduced overall synergy score impact by 15% for better power level distribution</li>
                                <li>
                                    Applied logarithmic scaling to commander synergy bonuses to prevent exponential
                                    growth
                                </li>
                                <li>Added speed multipliers based on card castability relative to curve</li>
                                <li>
                                    Improved consistency calculations with better tutor bias scaling based on deck
                                    competitiveness
                                </li>
                                <li>Added color identity bonuses (15-25%) to consistency for 4-5 color commanders</li>
                                <li>
                                    Reduced maximum draw score multiplier from 3x to 2x to prevent exponential scaling
                                </li>
                                <li>
                                    Enhanced commander synergy scaling with diminishing returns for broad trigger
                                    patterns
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            <ul>
                                <li>
                                    Added mass land denial combo detection like Living Plane + Elesh Norn and Living
                                    Plane + Linvala
                                </li>
                                <li>
                                    Improved bracket 1-2 boundary logic based on manabase quality for low power decks
                                </li>
                                <li>Refined bracket 4 calculation using averaged metrics with manabase bias</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card scoring</span>
                            <p />
                            <ul>
                                <li>Enhanced storm card scoring</li>
                                <li>Improved win condition scoring for mass creature deployment patterns</li>
                                <li>Better spot removal penalties for extremely narrow type restrictions</li>
                                <li>Reduced burn scores for single-use trigger/activation effects</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Deck import</span>
                            <p />
                            <ul>
                                <li>Added support for topdeck.gg deck imports</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Rule Zero Cards</span>
                            <p />
                            <ul>
                                <li>You can now print bracket specific cards</li>
                                <li>The original power level cards have various fixes and updates</li>
                            </ul>
                        </Text>
                    </ChangelogPane>

                    <ChangelogPane title="Platform Release: 08082025.0.4268" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 08.11.2025</span>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            <ul>
                                <li>
                                    Better detection of triggers that activate from opponent actions (draw, discard,
                                    etc.)
                                </li>
                                <li>Improved synergy recognition for blink and flicker effects</li>
                                <li>Fixed 'any number of' effects to properly count as synergies</li>
                                <li>Enhanced enabler detection for battlefield and hand effects</li>
                                <li>Better parsing of graveyard-to-hand and graveyard-to-battlefield interactions</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            <ul>
                                <li>Improved distinction between topdeck manipulation and draw effects</li>
                                <li>Better detection of variable damage effects for burn cards</li>
                                <li>Enhanced anthem detection including combat-triggered +1/+1 counter effects</li>
                                <li>Improved recursion detection for 'any number of' graveyard effects</li>
                                <li>Refined cheat category to better identify cost-bypass effects</li>
                                <li>Better categorization of lock effects as win conditions (like Kelsien combos)</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            <ul>
                                <li>Topdeck effects now properly count toward consistency scoring</li>
                                <li>Added per-card bonuses based on how much they enable the deck's strategy</li>
                                <li>Fixed scoring for effects that scale with number of opponents</li>
                                <li>Improved weighting for cards that support multiple deck categories</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            <ul>
                                <li>More improvements for parsing of 2 card combos (both early and late game)</li>
                            </ul>
                        </Text>
                    </ChangelogPane>

                    <ChangelogPane title="Platform Release: 06072025.0.8533" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 07.01.2025</span>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />I have added a{' '}
                            <b>
                                <i>vibe check</i>
                            </b>{' '}
                            for the determination of the WotC bracket rating, as it seems like the majority of WotC's
                            bracket system comes down to quantifying "your deck hurts my feelings" on a numerical scale.
                            <p />
                            Also fixed a few bugs in the UI for the brackets panel.
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 06072025.0.8184" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 06.25.2025</span>
                        <Text>
                            This is a small update around win condition scoring. Several categories were getting scored
                            too highly, which inflated a lot of deck scores.
                            <p />
                            Additionally there is a fix for brackets around sussing out the difference between bracket 4
                            and 5, as well as a fix for determining early combos.
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 06072025.0.6752" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 06.07.2025</span>
                        <Text>
                            The main focus on this update was overhauling the wincon system. This includes scoring of
                            combos, what types of wincons are counted, and how the overall wincon score effects the rest
                            of the deck power level score. This in turn effects what bracket the site thinks a deck
                            falls into.
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Power levels</span>
                            <p />
                            The following categories are now tracked as wincons:
                            <ul>
                                <li>Animate (think Bello and similar effects)</li>
                                <li>Anthem</li>
                                <li>Burn</li>
                                <li>Combos</li>
                                <li>General wincons like Coalition Victory</li>
                                <li>Group slug</li>
                                <li>Overrun</li>
                                <li>Poison (toxic, infect, and any other effects that assign poison counters)</li>
                                <li>Stompy</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            The bracket system has been updated:
                            <ul>
                                <li>
                                    Improved ability to suss out what combos should actually be counted as a two card
                                    combo
                                </li>
                                <li>
                                    Determination of if a deck is a 5 vs a 4 has been improved and looks at overall data
                                    for the deck, such as manabase and win conditions
                                </li>
                                <li>Burn</li>
                                <li>Combos</li>
                                <li>General wincons like Coalition Victory</li>
                                <li>Group slug</li>
                                <li>Overrun</li>
                                <li>Poison (toxic, infect, and any other effects that assign poison counters)</li>
                                <li>Stompy</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Manabase</span>
                            <ul>
                                <li>Slight tweak to the way that acceleration is being awarded</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card context data</span>
                            <ul>
                                <li>Fixes for determination of whether or not a card's effect references itself</li>
                                <li>Fixes for determination of card actions around combat and noncombat damage</li>
                                <li>Fixes for determination of card actions around attacking</li>
                                <li>Better parsing of what player or players an effect applies to</li>
                                <li>Better parsing of whether or not an effect is only usable once a turn</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card scoring</span>
                            <ul>
                                <li>Various changes and tweaks to better support the new wincon system</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card categories</span>
                            <p />
                            The following categories have been updated. The updates include improvements to category
                            detection as well as updates to the scoring for each category.
                            <ul>
                                <li>Draw</li>
                                <li>Recursion</li>
                                <li>Ramp</li>
                                <li>Spot removal</li>
                                <li>Stax</li>
                                <li>Animate</li>
                                <li>Anthem</li>
                                <li>Group slug</li>
                                <li>Overrun</li>
                                <li>Poison</li>
                                <li>Stompy</li>
                                <li>Voltron</li>
                                <li>Wincon</li>
                            </ul>
                            <p />
                            Additionally, these categories have been added:
                            <ul>
                                <li>Burn</li>
                                <li>Mill</li>
                            </ul>
                            <p />
                            One thing to call out here is that group slug was heavily refactored. It is a tricky
                            category because at its heart it is a stax effect. There is a lot of crossover with general
                            burn effects. I decided to try and keep effects that revolved around resource denial (think
                            Manabarbs) as group slug, and all other general damage effects as burn.
                        </Text>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <ul>
                                <li>
                                    Disabling synergy matches for group slug effects. We all know that manabarbs makes
                                    your lands ouchie
                                </li>
                                <li>Better parsing of what player or players an effect applies to</li>
                                <li>Fixed bug where keyword qualifiers were preventing correct matchings</li>
                                <li>
                                    Effects that check for damage done to opponents now register creatures in the deck
                                </li>
                                <li>Better handling of ward effects</li>
                                <li>Misc bug fixes</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Combos</span>
                            <ul>
                                <li>
                                    Combos now factor in additional cards and setup that might be required
                                    <ul>
                                        <li>
                                            The main goal here is to differentiate true "2 card combos" from ones that
                                            require a particular board state
                                        </li>
                                        <li>
                                            This is still a work in progress - I am sure there are some that are missed.
                                            Please ping me in the discord if you find any
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    The system now attempts to check if a deck has all of the requirements for a combo.
                                    For example, if it requires a creature with persist, and you don't have any, it will
                                    not add that combo to your deck
                                    <ul>
                                        <li>
                                            Again, this is a work in progress and I am sure there are some conditions
                                            that are missed. Please ping me in the discord if you find any
                                        </li>
                                    </ul>
                                </li>
                                <li>Better detection of additional mana cost required</li>
                                <li>Better determination of what things a combo does</li>
                                <li>
                                    Combo scoring updated. The following data points are now assessed to determine the
                                    score
                                    <ul>
                                        <li>Number of cards in the combo</li>
                                        <li>
                                            Total cmc of the combo, plus any additional costs discovered in the steps to
                                            do the combo
                                        </li>
                                        <li>Complexity of the combo (eg number of steps and requirements)</li>
                                        <li>How many tutors in the deck can get the various combo pieces</li>
                                        <li>Overall ability of the deck to draw into the combo pieces</li>
                                    </ul>
                                </li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 05012025.0.11794" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 05.30.2025</span>
                        This is a major update which focuses on scoring for manabase, cards, and power levels, as well
                        as numerous fixes for synergy and contextual mapping of card data.
                        <p />
                        <Text>
                            <span className={'header'}>Card Categories fixed</span>
                            <ul>
                                <li>Board wipes</li>
                                <li>Counterspells</li>
                                <li>Cost reduction</li>
                                <li>Draw</li>
                                <li>Mass land destruction</li>
                                <li>Other control</li>
                                <li>Overrun</li>
                                <li>Ramp</li>
                                <li>Recursion</li>
                                <li>Spot removal</li>
                                <li>Wincons</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Synergy updates/fixes</span>
                            <ul>
                                <li>Crew effects</li>
                                <li>Saddle effects</li>
                                <li>Crime abilities and triggerd</li>
                                <li>Effects that put cards on top of deck</li>
                                <li>Draw enablers for discard abilities</li>
                                <li>Self tap and untap effects</li>
                                <li>Investigate effects</li>
                                <li>Enablers and triggers for combat damage and other damage types</li>
                                <li>
                                    Counters
                                    <ul>
                                        <li>Better parsing of counter types (eg +1/+1, rad, etc)</li>
                                        <li>Cross matching of counter types for synergy</li>
                                        <li>Rad counters are now enablers for mill effects</li>
                                        <li>Better handling of proliferate</li>
                                    </ul>
                                </li>
                                <li>Effects that look for number of cards drawn by opponents</li>
                                <li>Better handling of conditions that look at base power and/or toughness</li>
                                <li>Loss of life effects and abilities</li>
                                <li>Copying and casting spells</li>
                                <li>Goad and Suspect effects and abilities</li>
                            </ul>
                            <Text>
                                <span className={'header'}>Archetypes updates/fixes</span>
                                <ul>
                                    <li>Parsing and handling of Kindred</li>
                                </ul>
                            </Text>
                            <Text>
                                <span className={'header'}>Manabase updates/fixes</span>
                                <ul>
                                    <li>
                                        Greatly improved analysis of the probability of a card being cast on any given
                                        turn
                                    </li>
                                    <li>Fetchlands in relation to mana fixing</li>
                                    <li>Hybrid mana parsing and handling</li>
                                    <li>
                                        Improved determination of what turn the mana produced by a card will be
                                        available
                                    </li>
                                    <li>
                                        Overall deck synergy is now factored into calculation of variable mana amounts
                                        for cards
                                        <ul>
                                            <li>An example would be Gaea's Cradle</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Suspend effects better accounted for
                                        <ul>
                                            <li>Suspend cost</li>
                                            <li>Suspend length</li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                            <Text>
                                <span className={'header'}>Card scoring updates/fixes</span>
                                <ul>
                                    <li>Scoring for planeswalkers adjusted</li>
                                    <li>
                                        Handling of higher cmc cards adjusted so that the score of the card will scale
                                        in relation to the deck's manabase quality
                                    </li>
                                    <li>
                                        Many changes to detection and handling of effects that alter the casting cost or
                                        activation cost
                                    </li>
                                    <li>Hybrid mana fixes</li>
                                    <li>Fixed parsing of chapters in sagas</li>
                                    <li>
                                        Fixes when adjusting scoring based on what types of synergistic effects it is
                                        reliant on
                                    </li>
                                </ul>
                            </Text>
                            <Text>
                                <span className={'header'}>Power levels</span>
                                <p />
                                Previously, the acceleration bonus from manabase scoring was applied on top of the
                                calculated power level as a bonus effect.
                                <p />
                                Now we look at every card in the deck, and add bonuses to the cards if they are castable
                                ahead of curve based on manabase analysis. This means that if you can cast a 5 drop on
                                turn 3, that 5 drop will score higher in your deck versus the same card in a slower
                                deck.
                                <p />
                                Other fixes include:
                                <p />
                                <ul>
                                    <li>Scoring of abilities for commander cards</li>
                                    <li>
                                        Cards that have generic " of the chosen type" style effects are now considered
                                        Kindred effects and get bonuses if the rest of the deck appears to be a Kindred
                                        archetype
                                    </li>
                                    <li>
                                        Improved handling of aggregated effects of the same type on any given card
                                        <ul>
                                            <li>This covers cards with "chose a mode" style effects</li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 04062025.1.8347" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 05.07.2025</span>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            This adds Brackets as a sortable column for all views.
                            <p />
                            <span className={'header'}>Note</span>
                            <p />
                            This is a somewhat small change for the look and feel of the site, however I used it as an
                            opportunity to do some long overdue maintenance on the database, which was quite extensive.
                            So if you see any weirdness with your decks, please jump in the discord and let me know!
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 03282025.0.787" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 04.22.2025</span>
                        <Text>
                            <span className={'header'}>Format changes</span>
                            <p />
                            Updates to conform to the changes in the lastet B&R and Brackets announcements
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 03282025.0.787" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 04.05.2025</span>
                        <Text>
                            <span className={'header'}>Card Categories</span>
                            <p /> This is a small update with updates for these card categories:
                            <ul>
                                <li>Board wipes</li>
                                <li>Spot removal</li>
                                <li>Draw</li>
                                <li>Ramp</li>
                                <li>Stax</li>
                                <li>Other control</li>
                            </ul>
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 02012025.1.4891" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 03.27.2025</span>
                        This is a large, site-wide release that focuses on user management (at long last). There are a
                        lot of changes to support this on both the website as well as the backend. Given that, there is
                        a good possibility of breakage. If you encounter anything, please feel free to join the discord
                        and let me know.
                        <p />
                        The next update will be focused on issues reported in the discord around manabase quality, card
                        categories, and synergy.
                        <p />
                        <Text>
                            <span className={'header'}>User Management</span>
                            <p />
                            You can now make an account for the site. This enables you to:
                            <ul>
                                <li>
                                    Link your accounts from deck building sites
                                    <ul>
                                        <li>
                                            This gives you control over your deck's visibilty on Commandersalt, and
                                            whether or not other people can import from your account on the other sites
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    Link your Patreon account for perks if you are a subscriber
                                    <ul>
                                        <li>There are only a few perks currently but I have ideas for more</li>
                                    </ul>
                                </li>
                                <li>
                                    Upload an avatar image
                                    <ul>
                                        <li>This overrides your avatar image from other sites</li>
                                    </ul>
                                </li>
                                <li>
                                    Change your display user name
                                    <ul>
                                        <li>This overrides your user name from other sites</li>
                                    </ul>
                                </li>
                                <li>Adjust visibilty of your profile, decks, etc</li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Caveats</span>
                            <ul>
                                <li>
                                    Currently you can only link accounts from the following sites:
                                    <ul>
                                        <li>Moxfield</li>
                                        <li>Archidekt</li>
                                        <li>Tappedout</li>
                                    </ul>
                                    <p />
                                    This means that if you primarily use something like Manabox, then making an account
                                    won't be of much use to you at this time. I want to expand this to support all of
                                    the deck building sites/tools, but I started with the above list primarily due to
                                    the fact that they have public facing profile data.
                                </li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 02012025.1.345" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 03.14.2025</span>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            Added logic to determine early game two card combos
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>General backend fixes</span>
                            <p />
                            <ul>
                                <li>Search results for authors pages</li>
                                <li>Changes to try and increase import speed</li>
                                <li>Fixes for horked card imports</li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>UI</span>
                            <p />
                            <ul>
                                <li>Overall look and feel</li>
                                <li>Brackets screen updates</li>
                                <li>Upgrades screen updates</li>
                                <li>Bug fixes</li>
                            </ul>
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 02012025.0.2958" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 02.22.2025</span>
                        <Text>
                            <span className={'header'}>Power Levels</span>
                            <p />
                            There have been a few changes to power level scoring. This is primarily driven by precons
                            getting scores above 4.9x.
                            <ul>
                                <li>
                                    The max value for synergy bonuses now takes into account things like card casting
                                    cost, ability restrictions and costs, etc. This change is mostly visible in synergy
                                    bonuses for commanders
                                </li>
                                <li>Max point value for evasion effects lowered</li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            <ul>
                                <li>
                                    Baseline (WotC definition) bracket 5: we are now attempting to suss out the 'intent'
                                    of the deck by looking at number of cEDH staples in the deck. If the deck meets
                                    criteria for baseline bracket 4 and has a high quantity of cedh staples, it is now
                                    classified as bracket 5.
                                </li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Categories</span>
                            <p />
                            Various fixes for the following
                            <ul>
                                <li>Boardwipes</li>
                                <li>Counterspells</li>
                                <li>Evasion</li>
                                <li>Ramp</li>
                                <li>Taxes</li>
                                <li>Other control</li>
                                <li>Draw</li>
                            </ul>
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 02012025.0.2460" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 02.18.2025</span>
                        <Text>
                            <span className={'header'}>Brackets</span>
                            <p />
                            We are now displaying calculated bracket scores. The guidelines set by WotC are great as a
                            beta, but definitely leave a lot of room for interpretation. To that end, we are showing 2
                            scores: one based on WotC's guidelines, and one based on Commandersalt's view of the deck.
                            <p />
                            The goal here is to make the brackets conversation more informative.
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Categories</span>
                            <p />
                            <ul>
                                <li>
                                    Land Denial
                                    <ul>
                                        <li>
                                            This is a new category. It is a subset of stax effects, and is used to
                                            quantify brackets scoring.
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    Various fixes for the following
                                    <ul>
                                        <li>Restock</li>
                                        <li>Other control</li>
                                        <li>Draw</li>
                                    </ul>
                                </li>
                            </ul>
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 02012025.0.1272" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 02.06.2025</span>
                        <Text>
                            <span className={'header'}>Categories</span>
                            <p />
                            <ul>
                                <li>
                                    Overrun
                                    <ul>
                                        <li>
                                            This is a new category. It covers effects that both pump your team as well
                                            as provide evasion. So, by that definition, Craterhoof is an overrun effect,
                                            and Beastmaster Ascension is not
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Power Levels</span>
                            <p />
                            <ul>
                                <li>Animate effects like Bello are now wincons</li>
                                <li>Overrun effects are handled/scored better</li>
                                <li>
                                    Decks that have commanders which have activated abilities which allow them to either
                                    draw or play from the top of their decks now gain bonuses if the deck also contains
                                    infinite mana combos
                                </li>
                            </ul>
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 02012025.0.732" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 02.05.2025</span>
                        <Text>
                            <span className={'header'}>Category Fixes</span>
                            <p />
                            <ul>
                                <li>Stax</li>
                                <li>Other Control</li>
                                <li>Discard</li>
                                <li>
                                    Animate
                                    <ul>
                                        <li>
                                            This is a new category. It covers effects like Bello, Bard of the Brambles
                                            and Living Plane which turn non-creature permanents into creatures
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Power Levels</span>
                            <p />
                            <ul>
                                <li>
                                    Discard effects now contribute to the interaction portion of power level scoring
                                </li>
                            </ul>
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 02012025.0.529" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 02.04.2025</span>
                        <Text>
                            <ul>
                                <li>
                                    Updated backend dependencies. No functionality changes, but hopefully performance
                                    gains
                                </li>
                                <li>Manuel further conflates subjective experience with unbiased reality</li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 12092024.1.1.6370" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 01.30.2025</span>
                        <Text>
                            <span className={'header'}>Salt Scoring</span>
                            <p />
                            <span className={'inlineHeader'}>Off color fetches</span> have been added to the salt
                            categories. These seem to unilaterally enrage (non cEDH) people for some reason. I don't
                            understand it, but there it is lol.
                            <p />
                            Also, <span className={'inlineHeader'}>discard effects</span> now contribute to the salt
                            score.
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Category Fixes</span>
                            <p />
                            <ul>
                                <li>Evasion</li>
                                <li>Spot Removal</li>
                                <li>Board Wipes</li>
                                <li>Wincons</li>
                                <li>Stax</li>
                                <li>Anthem</li>
                                <li>Other Control</li>
                                <li>Ramp</li>
                                <li>Tutor</li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Power Levels</span>
                            <p />
                            I made some changes to the calculations for synergy bonuses on non-combo wincons. This
                            change will only increase scores; it is not something that can lower scores. This is because
                            it is now giving points for synergy matches that were missed before.
                            <p />
                            Additionally there were some cards like Revel in Riches that were flagged as wincons, but
                            not contributing to the power level score.
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 12092024.1.1.5737" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 01.23.2025</span>
                        <Text>
                            The main thing of note in this release is that I have changed how the score for manabase
                            quality is applied to the deck's overall power level score.
                            <p />I have been trying to make a uniform scaling mechanism for casual through cEDH for
                            manabase quality, but I don't think that is really achievable. I have now pivoted towards
                            making a casual baseline score for manabase quality, as well as a more aggressive cEDH
                            baseline. The cEDH baseline has steeper requirements when it comes to number of fetches,
                            duals, basics, etc. In both cases this baselines are factored around the number of colors in
                            the deck.
                            <p />
                            <ul>
                                <li>cEDH manabase quality scoring is applied to any deck scoring 7.5 or higher</li>
                                <li>Casual manabase quality scoring is applied to everything under 7.5</li>
                            </ul>
                            <p />
                            The threshold is 7.5 currently because that is more or less the jumping off point for
                            optimizing towards cEDH. This line might move based on feedback going forwards.
                        </Text>
                        <Text>
                            <span className={'header'}>Power Levels</span>
                            <p />
                            <ul>
                                <li>
                                    Synergy bonuses for card effects are now weighted by the card's overall castability
                                    bias as well as the activation requirements for the effect, if applicable
                                </li>
                                <li>
                                    Fixes for bonuses for commander effects - some synergy scoring was being missed.
                                </li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            <ul>
                                <li>Various fixes around contextual mapping of card data</li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Category Fixes</span>
                            <p />
                            <ul>
                                <li>Draw</li>
                                <li>Recursion</li>
                                <li>Ramp</li>
                                <li>Spot Removal</li>
                                <li>Blink</li>
                                <li>Clone</li>
                                <li>Tokens</li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Manabase</span>
                            <p />
                            <ul>
                                <li>
                                    Effects like Urborg, Yavimaya, Prismatic Omen, etc are now included in manafixing
                                    score
                                </li>
                                <li>
                                    Land ramp effects like cultivate are now tracked. The additional lands available are
                                    factored into the hypergeometric calculations for mana availability based on what
                                    lands the individual pieces can get
                                </li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Deck Importing</span>
                            <p />
                            <ul>
                                <li>
                                    Archidekt importing improved - there were edge cases wherein the entire list was not
                                    being imported
                                </li>
                            </ul>
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 12092024.1.1.3690" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 01.19.2025</span>
                        <Text>
                            More tweaks to manabase scoring. This specifically applies to how the calculated manabase
                            score is applied to the overall deck power level. Generally speaking, decks should go up
                            slightly in score now.
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 12092024.1.1.3525" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 01.18.2025</span>
                        <Text>Small update this time :) More coming soon!</Text>
                        <Text>
                            <ul>
                                <li>Manabase fixes</li>
                                <li>Synergy fixes around goad</li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 12092024.1.1.2728" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 01.09.2025</span>
                        <Text>This is a smallish update around synergy and card categories.</Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 12092024.1.10009" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 12.31.2024</span>
                        <Text>
                            This update primarily focused on a large rewrite of the synergy cross-relational matching
                            system.
                        </Text>
                        <Text>
                            However, there are also changes to the Power Level scoring, and that is usually what people
                            get irritated about the most. So lets talk about that.
                        </Text>
                        <Text>
                            Precons are supposed to be the entry point to the format, and as far as the site is
                            concerned, I feel that precons should never score over 4.99x. Unfortunately, the new precons
                            are power crept pretty hard, which means that I had to adjust the grading criteria to be
                            more strict and keep the precons below the bar that I have set. This will cause people's
                            brews to be scored harsher. I am not sure what to do about that, but I feel like this is the
                            natural consequence of having the format power crept so much.
                        </Text>
                        <Text>
                            <b>
                                Additionally, non-tier (eg fringe) commanders are now capped at 9.29x. Previously, they
                                were capped at 8.99x.
                            </b>
                        </Text>
                        <p />
                        <Text>
                            <span className={'header'}>Manabase</span>
                            <p />
                            <ul>
                                <li>Adjustments to manafixing scoring</li>
                                <li>Adjustments to manabase quality scoring</li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <p />
                            <ul>
                                <li>Matching of player actions</li>
                                <li>Mapping and matching card actions</li>
                                <li>Tracking to and from zones</li>
                                <li>Mapping of type containers such as 'Mercenary'</li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Categories</span>
                            <p />
                            <ul>
                                <li>New category: Cost reduction</li>
                                <li>Lots of category fixes</li>
                            </ul>
                            <p />
                        </Text>
                        <Text>
                            <span className={'header'}>Power Levels</span>
                            <p />
                            <ul>
                                <li>Cost Reduction category added to efficiency scoring</li>
                                <li>Corrections/changes to wincon scoring</li>
                                <li>Bonuses applied to commander effects in specific categories based on synergy</li>
                            </ul>
                            <p />
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 10122024.2.6301" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 12.05.2024</span>
                        <Text>Buckle up, this is a big one.</Text>
                        <p />
                        <Text>
                            <span className={'header'}>Card Scoring</span>
                            <p />
                            This update overhauls the underlying scoring system used for cards on the site:
                            <p />
                            <ul>
                                <li>
                                    Every effect on a card is now weighted against the castability of the card (pip
                                    density, etc) as well as whatever activation costs might be required
                                </li>
                                <li>
                                    The scoring for every synergy relationship between cards is weighted by the same
                                    castability scoring for each card/effect
                                </li>
                                <li>Wincons are now also weighted by these same scoring effects</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Card Categories</span>
                            <ul>
                                <li>
                                    <b>Removal</b> is now split into two categories: <b>Spot Removal</b> and{' '}
                                    <b>Board Wipes</b>
                                </li>
                                <li>
                                    There is a new category called <b>Other Control</b>
                                    <ul>
                                        <li>A lot of effects have been incorrectly classified as Stax</li>
                                        <li>
                                            Going forwards, Stax will be specifically for effects that restrict or deny
                                            resources. And no, MLD is still not stax
                                        </li>
                                    </ul>
                                </li>
                                <li>A lot of general fixes to categorization</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Manabase</span>
                            <ul>
                                <li>
                                    The metric for quality has been updated to more accurately reflect usability of
                                    resources
                                    <ul>
                                        <li>
                                            A large part of this is better approximation of what turn a resource can be
                                            used on
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Combos</span>
                            <ul>
                                <li>Combos cache updated</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Power Levels</span>
                            <ul>
                                <li>
                                    Wincons
                                    <ul>
                                        <li>
                                            Scoring for individual cards is now weighted by the updated castability
                                            scoring
                                        </li>
                                        <li>
                                            Scoring for combos now takes into account the castability of each card in
                                            the combo
                                        </li>
                                        <li>
                                            Combos and individual cards now get bonuses if any of the cards can be
                                            cheated into play
                                        </li>
                                        <li>Broader set of anthem effects now considered wincons</li>
                                        <li>
                                            Most "pinger" effects are no longer wincons. I might revisit this later
                                            based on feedback
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    Manabase
                                    <ul>
                                        <li>
                                            The overall manabase score is used as a multiplier (from 0.1 to 1) against
                                            the rest of the deck's power level score. This has been updated so that it
                                            is less strongly applied on lower power level decks
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    Categories
                                    <ul>
                                        <li>
                                            <u>Consistency</u>: Previously there was a synergy bonus applied here. This
                                            has been removed since synergy is now used more granularly throughout the
                                            rest of the scoring
                                        </li>
                                        <li>
                                            <u>Efficiency</u>: Average CMC metric has been updated. Now cards get their
                                            mana value weighted by the number of cheat effects in the deck. They also
                                            get a slight weighting bonus based on the amount of ramp and fast mana in
                                            the deck. The net effect of this is that it lowers the decks average CMC
                                            value, which increases the score
                                        </li>
                                        <li>
                                            <u>Interaction</u>: <b>Removal</b> is replaced with <b>Spot Removal</b> and{' '}
                                            <b>Board Wipes</b>. Additionally, the <b>Other Control</b> subcategory is
                                            now included
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </Text>
                        <Text>
                            As always, there are some decks that are going to be adjusted too highly, and some that are
                            going to be unjustly cratered. This is an iterative process, and I greatly rely on feedback
                            from all of you via email and the Discord server.
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 10122024.2.677" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 11.09.2024</span>
                        <Text>Card category fixes</Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 10122024.2.188" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 10.31.2024</span>
                        <Text>cEDH metadata is back. Banned cards are filtered out of the result sets.</Text>
                        <Text>
                            Fixes for imports from:
                            <ul>
                                <li>tappedout.net</li>
                                <li>scryfall.com</li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 10122024.1.2658" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 10.28.2024</span>
                        <Text>
                            Fixes for imports from:
                            <ul>
                                <li>tappedout.net</li>
                                <li>topdecked.com</li>
                                <li>mtggoldfish.com</li>
                            </ul>
                        </Text>
                        <Text>
                            Additionally, there were a few other import errors (non-source dependent) that were fixed.
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 10122024.1" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 10.23.2024</span>
                        <Text>This is a fairly large update and aims to address some of the gaps in scoring:</Text>
                        <Text>
                            <span className={'header'}>Card Scoring</span>
                            <ul>
                                <li>Wincon scoring is more refined/nuanced</li>
                                <li>
                                    Card scoring is overhauled
                                    <ul>
                                        <li>Abilities scored better</li>
                                        <li>Better analysis of casting cost</li>
                                        <li>Snow mana handled</li>
                                        <li>Lots of individual category scoring fixes</li>
                                    </ul>
                                </li>
                                <li>Numerous card category fixes</li>
                            </ul>
                        </Text>
                        <Text>
                            There were also some bugs wherein card data was getting corrupted. This should be resolved
                            now but I will be keeping an eye on it.
                        </Text>
                        <Text>
                            <span className={'header'}>Power Levels</span>
                            <ul>
                                <li>
                                    Thresholds for several metrics lowered. This accounts for post ban meta as well as
                                    changes in internal card scoring.
                                </li>
                                <li>
                                    Powerlevel scoring adjusted by manabase score on decks with initial power level 3+
                                    (previously 5+).
                                </li>
                                <li>
                                    Commanders no longer have a static bonus on categories they are a part of. Instead,
                                    they now have no cap on the synergy bonus they get. In other words, if your
                                    commander is Kenrith, you dont get a massive boost for draw effects. However, if it
                                    is Yawgmoth, you probably do because there are so many lines to activate Yawgmoth
                                    with.
                                </li>
                                <li>Scoring of combos changed a bit</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>Note on cEDH meta</span>
                            <p />
                            There were some API changes to edhtop16.com and I have not had time to update my
                            implementation to account for it yet. I will hopefully have that done soon. Until then, I am
                            unable to update the meta standings.
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 08312024.2.1" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 09.24.2024</span>
                        <Text>
                            Patch to update the illegal checks for the new banned cards. Also, setting the score for
                            those cards to 0.1 to encourage updating decks.
                        </Text>
                        <Text>Additionally, the banned cards should no longer appear in the Upgradelizer results.</Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 08312024.2" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 09.23.2024</span>
                        <Text>Primarily fixes for card categories and synergy mappings.</Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 08312024.1" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 09.5.2024</span>
                        <Text>
                            <span className={'header'}>Synergy</span>
                            <ul>
                                <li>A lot of synergy interaction checks have been updated</li>
                                <li>"Any number of" cards are now accounted for</li>
                                <li>Synergy scoring is updated</li>
                            </ul>
                        </Text>
                        <Text>
                            <span className={'header'}>UI</span>
                            <ul>
                                <li>
                                    <nav
                                        className="jumpLink"
                                        onClick={() => {
                                            dispatch(setAppRoute({ route: `/archetypes` }));
                                        }}
                                    >
                                        Archetypes page is now available (still very beta)
                                    </nav>
                                </li>
                                <li>Synergy view now shows text for causal effects in triggers and enablers</li>
                                <li>General look and feel clean up and tweaks</li>
                            </ul>
                        </Text>
                    </ChangelogPane>
                    <ChangelogPane title="Platform Release: 08312024" isOpen={false}>
                        <span className={'deployDate'}>Deployed on: 08.31.2024</span>
                        <Text>
                            <span className={'header'}>Massive overhaul</span>
                            <p />
                            This change introduces better handling of MDFC/transform/split/etc cards. This required a
                            restructuring of the data structure definition for cards in the site, which resulted in
                            changes to pretty much every part of the backend.
                            <p />
                            So, TLDR; I would be very surprised if nothing broke after this update. Please feel free to
                            email me or join the discord to let me know! Thank you
                            <p />
                            <span className={'inlineHeader'}>
                                Also, I wanted to express my sincere gratitude to everyone who has contributed on the
                                Discord as well as through email.
                            </span>{' '}
                            It has been immensely helpful.
                        </Text>
                        <div style={{ minHeight: '20px' }} />
                        <Text>
                            <span className={'header'}>UI Updates</span>
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Card details page</span>
                            <p />
                            I have added a page that will give users insight into how card and mechanics are represented
                            data-wise internally to the site.
                            <p />
                            This will, of course, also making any parsing errors super obvious, but hopefully people
                            will use this as an opportunity to help out and let me know about these issues.
                            <p />
                            <nav
                                className="jumpLink"
                                onClick={() => {
                                    dispatch(setAppRoute({ route: `/cards` }));
                                }}
                            >
                                Check it out here!
                            </nav>
                        </Text>
                        <Text>
                            <span className={'subHeader'}>FAQ</span>
                            <p />I have, at long last, updated the FAQ page.
                            <nav
                                className="jumpLink"
                                onClick={() => {
                                    dispatch(setAppRoute({ route: `/faq` }));
                                }}
                            >
                                FAQ
                            </nav>
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Card list views</span>
                            <p />
                            Card list views across the site have been updated to clearly demarcate flip cards. The
                            back/flip side of each card is treated as a separate line "card" entity by the UI.
                            <p />
                            The reason for this is that since the site is checking the mechanics of individual lines of
                            the card, it was hard to tell which effects that were coming from the back side.
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Card preview popups</span>
                            <p />
                            Now when you click on a card to view its image, the dialog also contains a link to that
                            card's entry in the Cards page. If it is an mdfc/flip/whatever card, it will also show you
                            the front face.
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Commanders list</span>
                            <p />
                            There is now an additional option in the{' '}
                            <span className={'inlineHeader'}>sort dropdown</span> called "count". Selecting this will
                            sort the list by how many decks each commander has.
                            <p />I have also started adding thumbnail data that will appear in the list of commanders.
                            This data will fill in as people use the site (eg. I'm not reindexing every commander on the
                            site to get the appropriate thumbnails)
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Authors list</span>
                            <p />
                            The Authors page also has the updated <span className={'inlineHeader'}>sort dropdown</span>,
                            which allows you to sort by the number of decks each author/user has submitted.
                        </Text>
                        <div style={{ minHeight: '20px' }} />
                        <Text>
                            <span className={'header'}>Platform (backend) Updates</span>
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Card categories</span>
                            <p />
                            Many categories have been updated/corrected.
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Synergy</span>
                            <p />
                            Synergy detection has been improved. As always though, fixing one part of this usually
                            results in issues with cards that I did not think to check. Please let me know if there's
                            any regressions here.
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Power levels - top scoring decks</span>
                            <p />
                            There was a bug in the way that the top .001% of the decks were calculated, which lead to
                            many decks being higher than they should have been.
                            <p />
                            <span className={'inlineHeader'}>
                                The amount that these decks were off was usually at in the range of .0001 - .001.
                            </span>
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Power levels - Wincons</span>
                            <p />
                            Many cards that were not previously recognized as wincons are now handled.
                            <p />
                            Additionally, the scoring for wincons has been adjusted - individual cards were getting far
                            too high of scores, which was throwing off the curve.
                        </Text>
                        <Text>
                            <span className={'subHeader'}>Manabase Scoring</span>
                            <p />
                            I found some issues with the manabase calculations for casting things on curve as well as
                            early game acceleration. For a lot of decks, the difference is nil. For others, it could go
                            up or down a little bit.
                            <p />I also found an issue wherein the manabase score would change by a fraction of a
                            decimal point occasionally on reimport. This has been fixed. The problem was introduced when
                            I was trying to cache hypergeometric distribution calculations to accelerate import time.
                        </Text>
                    </ChangelogPane>
                    <>
                        <ChangelogPane title="Platform Release: 07282024.4" isOpen={false}>
                            <span className={'deployDate'}>Deployed on: 08.20.2024</span>
                            <Text>
                                <span className={'subHeader'}>Card scoring updates</span>
                                <p />
                                There are updates to scoring in a lot of different card categories. Some things went
                                higher, others lower. The goal is to more accurately reflect the mechanical value of the
                                card.
                                <p />
                                <span className={'subHeader'}>Card categories</span>
                                <p />
                                The following categories have been updated:
                                <ul>
                                    <li>Tutors</li>
                                    <li>Draw</li>
                                    <li>Removal</li>
                                    <li>Fast mana</li>
                                    <li>Counterspells</li>
                                    <li>Ramp</li>
                                    <li>Spot Removal</li>
                                    <li>Boardwipes</li>
                                    <li>Mass land destruction (MLD)</li>
                                    <li>Theft</li>
                                    <li>Stax</li>
                                    <li>Restock (Timetwister effects)</li>
                                </ul>
                                <p />
                                <span className={'subHeader'}>Manabase</span>
                                <p />
                                Last week I accidentally introduced some issues into the manabase algorithm when I was
                                making performance optimizations. This has been fixed. Also, the value of basic lands in
                                relation to manabase quality has been lowered.
                                <p />
                                <span className={'subHeader'}>Power level updates</span>
                                <p />
                                There were some issues where the synergy bonuses for cards pushed their score above the
                                max value for a given category. This has been fixed.
                                <p /> Also, the value of synergy in relation to consistency scoring has been changed for
                                higher power levels.
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 07282024.3" isOpen={false}>
                            <span className={'deployDate'}>Deployed on: 08.10.2024</span>
                            <Text>
                                <span className={'subHeader'}>General maintenance</span>
                                <p />
                                The past week has been focused on trying to make some database optimizations in order to
                                cut down on expenses. This lead to some pretty wide ranging issues which should now be
                                resolved.
                                <p />
                                Additionally, some decks were displaying crazy high combo scores. This has been fixed
                                and now the correct score is showing. This is purely a display issue; it should not
                                effect the actual deck scores.
                                <p />
                                Thanks for your patience!
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 07282024.1" isOpen={false}>
                            <span className={'deployDate'}>Deployed on: 08.02.2024</span>
                            <Text>
                                <span className={'subHeader'}>Bug fixes</span>
                                <p />
                                This update primarily focused on bug fixes in the following areas:
                                <p />
                                <ul>
                                    <li>Archetype detection</li>
                                    <li>Card category assignment</li>
                                    <li>Contextual card parsing</li>
                                    <li>Synergy mappings</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 07282024" isOpen={false}>
                            <span className={'deployDate'}>Deployed on: 07.28.2024</span>
                            <Text>
                                <span className={'subHeader'}>Archetypes!</span>
                                <p />
                                This is an idea that I have been wanting to add to the site since the beginning, but it
                                wasn't until now that I was able to generate enough supporting data for a deck to even
                                begin to attempt to derive the deck's archetype.
                                <p />
                                The basic idea is that the system will try to classify the deck under the classic
                                archetypes of Midrange, Control, and Combo. Aggro is basically combined with Midrange
                                because, and this is just my opinion, Aggro as an archetype more directly applies to two
                                player formats.
                                <p />
                                <span className={'inlineHeader'}>
                                    I am sure there are archetypes that are missing or misrepresented.
                                </span>
                                <p />
                                If you see such a case, PLEASE let me know on either discord or the email form on the
                                site. I do very much respect everyone's right to sh!tpost on Farcebook, Reddit, and/or
                                Discord, but nothing really changes unless I know directly ;)
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 07092024.3" isOpen={false}>
                            <span className={'deployDate'}>Deployed on: 07.20.2024</span>
                            <Text>
                                <span className={'subHeader'}>Power Levels</span>
                                <p />
                                This update tries to balance out the casual scores a bit more.
                                <p />
                                As far as the scores from the site are concerned,
                                <span className={'inlineHeader'}>
                                    precons are the baseline that casual decks are measured against, and precons should
                                    be under a 5
                                </span>
                                .
                                <p />
                                Trying to balance this without knocking down cEDH decks and also not accidentally
                                increasing other decks scores unnecessarily (that word is unnecessarily difficult to
                                spell, tbh lol), has been a constant challenge.
                                <p />
                                With that said, the following has been updated:
                                <p />
                                <ul>
                                    <li>
                                        Consistency scores are weighted by the number of tutors in the deck. Let's be
                                        real - 99% of the "this is a competitive list" revolve around the presence of
                                        tutors
                                    </li>
                                    <li>Combo score aggregation is changed slightly</li>
                                    <li>Wincon scoring updated</li>
                                </ul>
                                <p />
                                Additionall, the power levels graph in the UI has been updated to use the same
                                categories for casual that are used for cEDH. The baseline values are still lower for
                                casual, though.
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 07092024.2" isOpen={false}>
                            <span className={'deployDate'}>Deployed on: 07.17.2024</span>
                            <Text>
                                <span className={'subHeader'}>Category detection fixes and improvements</span>
                                <p />
                                <ul>
                                    <li>Ramp</li>
                                    <li>Removal</li>
                                    <li>Stax</li>
                                    <li>Manafixing</li>
                                    <li>Draw (added cascade and discover, as they are forms of card advantage)</li>
                                    <li>Fast mana</li>
                                    <li>Tutors</li>
                                    <li>Theft</li>
                                    <li>Graveyard</li>
                                    <li>Boardwipes</li>
                                    <li>Wincons</li>
                                </ul>
                                <p />
                                <span className={'subHeader'}>Synergy</span>
                                <p />
                                <ul>
                                    <li>Goad - tracks creature token creation effects that affect other players</li>
                                    <li>Token multiplier effects (such as Doubling Season)</li>
                                    <li>Better tracking of ETB effects</li>
                                    <li>General parsing improvements/fixes</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 07092024.1" isOpen={false}>
                            <span className={'deployDate'}>Deployed on: 07.12.2024</span>
                            <Text>
                                <span className={'subHeader'}>Printable "Rule Zero" cards</span>
                                <p />
                                The original intent of adding power level calculations to the site was to try and
                                facillitate rule zero conversations in playgroups.
                                <p />
                                To that end, I have added a way to download a printable "rule zero card" that can be
                                downloaded from a deck's details page. The downloaded file is in PDF format, and is made
                                to be the same size/shape as a standard MtG card. For myself, I print these out and
                                stick them in my deck boxes :)
                                <p />
                                Hopefully other people find this useful too! I will be adding a way to bulk print these
                                for all of a user's decks in the near future.
                                <p />
                                Example from my own deck:
                                <p />
                                <img src={`resources/rule_zero_card_example.png`} width={400} />
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 07092024" isOpen={false}>
                            <span className={'deployDate'}>Deployed on: 07.09.2024</span>

                            <Text>
                                <span className={'subHeader'}>Wincons</span>
                                <p />
                                This update introduces the concept of "wincons" to the power level algorithm. <p />
                                The algorithm has always looked at combos that win the game, but now it is also looking
                                at ancillary conditions such as Craterhoof Behemoth in a go wide deck.
                                <p />
                                The main point of this is two fold: increase the rankings of decks that are rated lower
                                than they actually are, and also filter out decks that are incorrectly marked too
                                highly.
                                <p />
                                This is a departure from the previous design wherein combos counted towards consistency
                                and directly/automatically affected the power level score. That system didn't scale well
                                though, as it simultaneously rewarded decks with a bunch of combos and no way to play
                                them, and also unfavorably skewed the scoring for lower power level decks.
                                <p />
                                So now, this is what we are looking at:
                                <p />
                                <ul>
                                    <li>
                                        <span className={'inlineHeader'}>
                                            Decks with power level less than 7 (in other words, "casual")
                                        </span>
                                        <p />
                                        For these decks, the wincons are used as a bonus on top of the score calculated
                                        from the consistency, efficiency, and interaction scores. The bonus is
                                        formulated in such a way as to not push the deck's score above 7.5.
                                    </li>
                                    <p />
                                    <li>
                                        <span className={'inlineHeader'}>Decks that score above 7</span>
                                        <p />
                                        Decks in this range are ones that are, in my opinion, on the bleeding edge of
                                        unplayable in casual pods, yet not really viable in high power to cEDH pods.
                                        That creates a situation where the only real way to play them is to keep
                                        optimizing them, which leads to cEDH levels of building.
                                        <p />
                                        With that in mind,{' '}
                                        <span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                                            in power level 7+ decks, wincons are used as a scaling attribute instead of
                                            a bonus.
                                        </span>
                                        <p />
                                        For example: A deck has a power level score of 9.2, because it has a lot of
                                        interaction, low cmc, lots of card draw, etc, and also a solid manabase.
                                        Mechanically there is nothing wrong with the deck. <b>However</b>, the deck also
                                        has no real way of closing out games. There are no game ending combos, no combat
                                        damage tricks, nothing that will accelerate the game towards a resolution. This
                                        tends to happen a lot of with stax decks (I am super guilty of this).
                                        <p />
                                        So in this case, the deck has a score of 9, but a wincon score of say 50. 50 is
                                        ~16% of the base value for wincons in cEDH (as far as the site is concerned). So
                                        what we do then is take the amount over 7 that the deck is and multiply that by
                                        0.16.
                                        <p />
                                        In other words...
                                        <ul>
                                            <li>
                                                the deck in this example scores 9.2 based on consistency, efficiency,
                                                interaction, and manabase
                                            </li>
                                            <li>
                                                it has 16% of the wincons it should to be able to close out games
                                                reliably
                                            </li>
                                            <li>we subtract 7 from 9.2, which is obviously 2.2</li>
                                            <li>we multiply that by .16</li>
                                            <li>this gives us 0.352</li>
                                            <li>we then add that value to 7, yielding 7.352</li>
                                            <li>and that is the final power level score for the deck</li>
                                        </ul>
                                    </li>
                                </ul>
                                There will, of course, be ongoing refinements to this as we go forward; I know that not
                                all wincons are represented. One of the most glaring omissions currently is voltron.
                                That being said, please do hop in the discord and let me know if there is something that
                                needs to be updated and/or added!
                                <p />
                                <span className={'subHeader'}>Synergy</span>
                                <p />
                                All of the wincon calculations are extremely reliant on synergy correlations. In order
                                to do that, a lot of changes/fixes were made:
                                <ul>
                                    <li>Better mappings of zone effects, such as leaving hand to battlefield, etc</li>
                                    <li>Improved tracking of player ownership of effect</li>
                                    <li>
                                        Some card actions require other actions to be done in order for the effect to
                                        work. For example: damage doublers. The underlying logic for this has been
                                        added, but there are many dependencies not yet tracked
                                    </li>
                                    <li>Checks for supertypes such as legendary, snow, etc, improved</li>
                                    <li>
                                        Improved handling of these triggers:
                                        <ul>
                                            <li>Enter the graveyard</li>
                                            <li>Die</li>
                                            <li>Draw</li>
                                            <li>Countering spells or abilities</li>
                                        </ul>
                                    </li>
                                    <li>Many small fixes and tweaks for effect mappings</li>
                                </ul>
                                <p />
                                <span className={'subHeader'}>Manabase</span>
                                <p />
                                For manabases, I re-introduced manafixing to the scoring. It was originally part of the
                                calculations, but I removed it when I overhauled everything. Now that it's been out in
                                the wild and I've had some time to look at the results, I think that it was incorrect to
                                remove it.
                                <p />
                                More importantly,{' '}
                                <span className={'inlineHeader'}>
                                    early game acceleration is now used as a bonus for the manabase score.
                                </span>{' '}
                                I am hoping that this scales better across casual lists. cEDH lists pretty much always
                                have high scores in manabase, so this doesn't really affect them.
                                <p />
                                Additionally, I slightly increased the score deduction for etb tapped lands.
                                <p />
                                <span className={'subHeader'}>Power Levels</span>
                                <p />
                                Most of the changes here were mentioned in the wincons section, but I also updated:
                                <ul>
                                    <li>Interaction now includes the 'evasion' category</li>
                                    <li>Scoring for several card categories has been updated</li>
                                    <li>Weighting of ramp effects in Efficiency has been lowered</li>
                                </ul>
                                <p />
                                <span className={'subHeader'}>Deck Importing</span>
                                <p />
                                Importing from Archidekt, Deckstats, and TopDecked has been improved.
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 06282024" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 06.28.2024</i>
                                </b>
                            </span>
                            This major update includes a substantial set of changes to synergy. Additionally there are
                            fixes and stabilization for manabase and card categories.
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>Better analysis and scoring of early game acceleration</li>
                                </ul>
                                <p />
                                <span style={{ fontWeight: 'bolder' }}>Categories</span>
                                <ul>
                                    <li>Fixes for removal</li>
                                    <li>General improvements in parsing</li>
                                </ul>
                                <p />
                                <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                                <ul>
                                    <li>Better handling of "choose" effects</li>
                                    <li>Support for effects that require a commander in play</li>
                                    <li>Better handling of tokens</li>
                                    <li>Generic fallback conversion to super type for type comparisons</li>
                                    <li>Fixes for handling legendary types</li>
                                    <li>Improved handling of cases where multiple types should be accepted</li>
                                    <li>Better detection and comparison of zone dependencies that card effects have</li>
                                    <li>
                                        Better detection and comparison of player dependencies that card effects have
                                    </li>
                                    <li>Support for catching "cast" and "play" triggers from zones such as exile</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 06142024.1 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 06.15.2024</i>
                                </b>
                            </span>
                            Whoops, apparently a lot of decks got absolutely dunked on with the 06142024 update. Sorry
                            about that!
                            <p />
                            This incremental update has some slight tweaks to power level and manabase analysis to try
                            and course correct that a bit.
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 06142024" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 06.14.2024</i>
                                </b>
                            </span>
                            This update focuses on updating the power level system - the main goal is to more accurately
                            assess decks on the lower end of the scale. Hopefully this yields a net positive improvement
                            for people.
                            <p />
                            There also various bug fixes for manabase scoring, synergy, and card categories.
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.9 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 06.13.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>
                                        Reverting 'fix' from previous release; I misidentified the issue, and this was
                                        not the root cause.
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.8 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 06.13.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>
                                        Analysis of early game acceleration was horked - the calculated value was
                                        greatly inflated. This lead to deck scores being higher than they should have
                                        been. This has been fixed.
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.7 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 06.11.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Salt</span>
                                <ul>
                                    <li>
                                        The detection rules for the following salt categories have been improved:
                                        <ul>
                                            <li>Board wipes</li>
                                            <li>Mass land destruction</li>
                                        </ul>
                                    </li>
                                </ul>
                                <p />
                                <span style={{ fontWeight: 'bolder' }}>Card categories</span>
                                <ul>
                                    <li>
                                        The detection rules for the following card categories have been improved:
                                        <ul>
                                            <li>Counterspells</li>
                                            <li>Ramp</li>
                                            <li>Recursion</li>
                                            <li>Tutors</li>
                                        </ul>
                                    </li>
                                </ul>
                                <p />
                                <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                                <ul>
                                    <li>
                                        Correlation between token types and effects (such as Goldspan Dragon and
                                        treasures) has been improved
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.6 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 05.30.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                                <p />
                                I have been making a lot of tweaks in this area the last week or so to try and balance
                                out the precons vs cedh lists, which involved a lot of fiddling with manabase
                                acceleration and individual power level metrics. Unfortunately, this has created a lot
                                of fluctuating scores, so I apologize if the results were confusing. Thanks for hanging
                                in there with me haha.
                                <p />
                                After this update, the precons are scoring on average a bit higher than I would like,
                                but my attempts to try and keep them at lower scores have been punishing other decks a
                                bit too harshly.
                                <p />I am going to let this sit for a bit and see how the scores look for a few weeks
                                before making more changes here, and instead focus on bug fixes.
                                <ul>
                                    <li>Manabase calculations for early acceleration changed to be more realistic</li>
                                    <li>
                                        Power level thresholds for pretty much all categories have been adjusted to be
                                        more fair
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.6 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 05.29.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                                <ul>
                                    <li>
                                        The method I was capping the scores with was making some decks get marginally
                                        higher scores than they should have (eg .00x higher). This has been fixed.
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.5 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 05.28.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>
                                        Early game acceleration now looks at turns 1 and 2. Previously it looked at
                                        turns 1, 2, and 3
                                    </li>
                                    <li>Quality assessment softened slightly</li>
                                    <li>Hybrid mana analysis fixed</li>
                                </ul>
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                                <ul>
                                    <li>
                                        The weightings for the different power level subcategories have been changed to
                                        try and better balance precon through cedh
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.4 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 05.27.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>
                                        Ramp and fastmana weightings and bonuses have been tweaked to try and better
                                        represent actual probability
                                    </li>
                                    <li>Method for determining unconditional etb tapped lands improved</li>
                                    <li>
                                        Negative weighting on manabase probability and quality scores improved/tweaked
                                        to be more fair
                                    </li>
                                </ul>
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                                <ul>
                                    <li>Changes to weighting for combos and answers</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.3 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 05.23.2024</i>
                                </b>
                            </span>
                            <Text>
                                This update focuses on bug fixes in categories, as well as refining the manabase scoring
                                and deck power level analysis algorithm.
                                <p />
                                As always, it is a continuous balancing act of trying to keep precons &lt;&#61; 4
                                without lowering the mid/high/cedh decks unjustly. Hopefully your deck wasn't caught in
                                the crossfire... but if it was, my apologies. Nobody likes having their decks scored
                                lower than what they personally believe they are, but at the end of the day, some decks
                                have to fall in the 1-3 range. Otherwise the whole scale is meaningless.
                                <p />
                                <i>OBLIGATORY:</i> I didn't come up with the 1-10 scale; I am just trying to implement
                                it 🤣
                                <p />
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>Changed weighting for etb tapped lands for mana distribution curves</li>
                                    <li>Changed application of weights for fast mana and ramp on pip calculations</li>
                                    <li>Added additional support for commander ramp bonuses in distribution curves</li>
                                    <li>Other bug fixes and tweaks</li>
                                </ul>
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                                <ul>
                                    <li>Manabase now has a higher max weighting on overall score</li>
                                    <li>
                                        <Text>
                                            <span style={{ fontWeight: 'bolder' }}>Card scoring and synergy</span>
                                            <ul>
                                                <li>
                                                    Changed manner in which bonus is calculated for commander synergy
                                                    within given categories
                                                </li>
                                            </ul>
                                        </Text>
                                    </li>
                                    <li>
                                        <Text>
                                            <span style={{ fontWeight: 'bolder' }}>Consistency</span>
                                            <ul>
                                                <li>Adjusted weightings for tutors, draw, and recursion</li>
                                                <li>
                                                    Changed how combo scoring is aggregated; increased weighting for
                                                    combo score
                                                </li>
                                            </ul>
                                        </Text>
                                    </li>
                                    <li>
                                        <Text>
                                            <span style={{ fontWeight: 'bolder' }}>Efficiency</span>
                                            <ul>
                                                <li>Changed fast mana score aggregation; increased weighting</li>
                                                <li>Increased weighting for average cmc (base value is 1.26)</li>
                                                <li>Weighting for synergy is lowered</li>
                                            </ul>
                                        </Text>
                                    </li>
                                </ul>
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Card Categories</span>
                                <p />
                                The following categories have been updated/improved:
                                <ul>
                                    <li>Fast mana</li>
                                    <li>Ramp</li>
                                    <li>Recursion</li>
                                    <li>Removal</li>
                                    <li>Taxes</li>
                                    <li>Stax</li>
                                    <li>Counter spells</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.2 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 05.14.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>
                                        More tweaks to algorithm to account for pip density and generic mana quantity
                                        relative to ramp and fast mana available on a given turn.
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024.1 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 05.13.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>
                                        This update has some tweaks to the manabase scoring around impact of ramp and
                                        fast mana
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 05102024" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 05.10.2024</i>
                                </b>
                            </span>
                            <Text>
                                This is a large update that revolves around redoing how the manabase analysis is scored.
                                As with all of the updates of this nature, I am sure there will be a decent amount of
                                score changes across the board. I don't like seeing peoples scores go down, but there
                                does need to be a floor, unfortunately.
                                <p />
                                Hopefully the net result will be that decks were rated too highly are now rated closer
                                to where they should be. I don't expect to see much movement with the high power and
                                cEDH decks.
                                <p />
                                If you are interested in how the card probability is determined,{' '}
                                <u>
                                    <a
                                        href={'https://en.wikipedia.org/wiki/Hypergeometric_distribution'}
                                        target={'_blank'}
                                    >
                                        check this out.
                                    </a>
                                </u>
                                <p />
                                With that said, we now look at three things:
                                <ul>
                                    <li>
                                        Acceleration
                                        <ul>
                                            <li>
                                                This score revolves entirely around how quickly your deck can cast
                                                things on turns 1 through 3.
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Playing on curve
                                        <ul>
                                            <li>
                                                For this, we look at how much of your deck can be played on a per turn
                                                basis versus what the ideal curve would be. The{' '}
                                                <i>
                                                    <b>ideal curve</b>
                                                </i>{' '}
                                                follows the traditional rule of casting 100% of your one-drops on turn
                                                1, 100% of your two-drops on turn 2, etc.
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Manabase quality
                                        <ul>
                                            <li>
                                                Here we are looking at all mana sources in the deck, and evaluating
                                                based on the following
                                                <ul>
                                                    <li>Does it enter tapped?</li>
                                                    <li>
                                                        How much mana does it generate vs what is its mana value? (in
                                                        other words, net mana produced)
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 03302024.8 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 04.26.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                                <ul>
                                    <li>Lots of fixes for cross-dependency mapping</li>
                                    <li>
                                        Scoring
                                        <ul>
                                            <li>Triggers for commanders are now scored much higher.</li>
                                            <li>Other synergies for commanders are also scored higher.</li>
                                            <li>Enablers are scored lower</li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 03302024.7 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 04.25.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                                <ul>
                                    <li>
                                        Efficiency score now gets a bump from synergy if the synergy score meets certain
                                        requirements.
                                    </li>
                                    <li>Recursion has been added as an additional factor for Consistency.</li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Card Scores and Categories</span>
                                <ul>
                                    <li>
                                        Changes for calculations around castability requirements based on mana pips.
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 03302024.6 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 04.20.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                                <ul>
                                    <li>
                                        Stax and Taxes: the overall value of these categories relative to other
                                        categories under Interaction have been lowered (sorry Winota. Your time has
                                        passed 🙁)
                                    </li>
                                    <li>
                                        Combos: Value of combos relative to other powerlevel scores has been increased
                                        (High five, Teshar!).
                                    </li>
                                    <li>
                                        Other small tweaks to level set based on feedback I have gotten from the high
                                        table.
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 03302024.5 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 04.15.2024</i>
                                </b>
                            </span>
                            <Text>
                                I have noticed that some decks were getting a bit too much of a bump from the recent
                                synergy bonus changes.
                                <p />
                                This update has various fixes to try and address that.
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                                <ul>
                                    <li>Interaction: the baseline threshold for this has been increased.</li>
                                    <li>Synergy bonuses: bonus for synergy of all types has been decreased</li>
                                </ul>
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                                <ul>
                                    <li>Multiple fixes for parsing types for synergy context maps</li>
                                    <li>
                                        More nuanced lookup for synergy for specific card effects
                                        <ul>
                                            <li>
                                                Removal, stax, and taxes no longer look for synergy for their respective
                                                effects. We don't really need bonuses for Trinisphere 🤣
                                                <p />
                                                <b>Note:</b> Synergy for activation costs for removal should still be
                                                counted.
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 03302024.4 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 04.11.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power levels</span>
                                <ul>
                                    <li>
                                        Bonus for high synergy with deck commanders
                                        <ul>
                                            <li>
                                                <Text>
                                                    There are some high power builds that have few to none deterministic
                                                    combos which have been being score unfairly low. This update
                                                    attempts to correct that by giving a bonus based on the number of
                                                    synergistic effects of cards that synergize with the commanders in
                                                    the deck.
                                                    <p />
                                                    The idea is that this will help account for non-deterministic combos
                                                    as well as increasing the value of high synergy lists.
                                                </Text>
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        "Fringe cEDH"
                                        <ul>
                                            <li>
                                                <Text>
                                                    The definition of fringe as far as the is concerned has now changed.
                                                    A lot of the decks that were classified as "fringe" were perfectly
                                                    viable as cEDH, but had no recent meta representation. The vast
                                                    majority of them are now allowed into the cEDH scores (capped at 9.2
                                                    though).
                                                </Text>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 03302024.3 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 04.11.2024</i>
                                </b>
                            </span>
                            <Text>
                                I have been doing multiple patch releases almost every evening to adjust scores and
                                categorizations, as these errors are much more visible now with the Upgradelizer
                                function.
                                <p />
                                Oh yea, theres a new feature - the Upgradelizer! Its super beta, but the idea is to find
                                cards that might help you upgrade your deck. It doesn't show cards that are already in
                                your deck (duh). I have a ton of ideas for it. Eventually I want to recommend direct
                                swaps for lower quality cards in a decklist.
                                <p />
                                Anyways... this update is specific to manabase scoring.
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>
                                        Urza, Lord High Artificer style mana production effects
                                        <ul>
                                            <li>
                                                The manabase scores for these decks did not reflect the value of his
                                                ability. This should also carry over to any card with similar
                                                abilities... eg tap an untapped ____ to add ___.
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 03302024.2 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 04.02.2024</i>
                                </b>
                            </span>
                            <Text>
                                Recent changes caused the precon power levels to get pushed up into the 5 and 6 power
                                level range. This is a problem because precons are meant to represent a pretty low bar
                                for deck power levels.
                                <p />
                                This release is focused on course correcting things that were artificially inflating
                                their scores:
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Card scores</span>
                                <ul>
                                    <li>Casting cost (generic and colored pips) are now more heavily weighted</li>
                                    <li>Removal - scoring now reflects target type</li>
                                    <li>Draw - abilities that require sacrificing itself are now scored lower</li>
                                    <li>
                                        Ramp - triggered abilities that generate treasure (and gold/powerstone) tokens
                                        are now scored higher
                                    </li>
                                    <li>
                                        Ramp - harsher evaluation of cost to benefit ratio when looking at mana
                                        production scoring
                                    </li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <ul>
                                    <li>Taplands are scored more severely</li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                                <ul>
                                    <li>Efficiency now has a higher baseline score</li>
                                </ul>
                            </Text>
                            <Text>
                                Additionally, there were some bug fixes made to the following category detection
                                algorithms:
                            </Text>
                            <ul>
                                <li>Graveyard</li>
                                <li>Tutors</li>
                                <li>Ramp</li>
                                <li>Removal</li>
                                <li>Fast mana</li>
                            </ul>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 03302024.1 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 04.01.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Card categories</span>
                                <ul>
                                    <li>
                                        The following categories have been improved:
                                        <ul>
                                            <li>Ramp</li>
                                            <li>Fast mana</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Additionally, I added "Theft" as a category (previously it was only under Salt)\
                                    </li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                                <ul>
                                    <li>
                                        Lots of fixes to relational matches of cross-dependencies between synergy maps
                                    </li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                                <ul>
                                    <li>
                                        Fixed a bug where synergy bonuses were pushing some cards above the max score
                                        per card
                                    </li>
                                    <li>
                                        Slightly lowered baseline scores for efficiency to match new scoring algorithm
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 03302024" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 03.30.2024</i>
                                </b>
                            </span>
                            <Text>
                                This update focuses on a complete overhaul of the card scoring system.
                                <p />
                                Additionally, we are now adding bonuses for card synergy, which is applied to deck
                                scoring.
                                <p />
                                There is pretty much an infinite amount of things that can go wrong here, so don't be
                                surprised if you see your scores jumping around a bit over the next couple of weeks as I
                                fine tune it :)
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 02162024.3 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 03.12.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Card categories</span>
                                <ul>
                                    <li>
                                        Blink
                                        <ul>
                                            <li>Better aggregation of effects</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Recursion
                                        <ul>
                                            <li>Blink effects no longer count as recursion</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Removal
                                        <ul>
                                            <li>Blink effects no longer count as removal</li>
                                            <li>
                                                Effects that put -1/-1 counters on itself (such as Shield Sphere) no
                                                longer counted as removal
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Graveyard
                                        <ul>
                                            <li>
                                                Effects that remove itself from a graveyard no longer count as graveyard
                                                interaction
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Draw
                                        <ul>
                                            <li>Better handling of reveal effects like Atraxa, the Grand Unifier</li>
                                            <li>Better aggregation of other draw keywording</li>
                                        </ul>
                                    </li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Card context mapping</span>
                                <ul>
                                    <li>Better parsing of trigger effects with 'for each' wording</li>
                                    <li>Fix for self-reference checks on flip cards</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 02162024.3 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 03.05.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Importing</span>
                                <ul>
                                    <li>
                                        Deckstats
                                        <ul>
                                            <li>
                                                Reverted recent change to Deckstats imports - things should be working
                                                correctly again.
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 02162024.2 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 02.24.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Card categories</span>
                                <ul>
                                    <li>
                                        Stax
                                        <ul>
                                            <li>Ward effects no longer count as stax</li>
                                            <li>Auras like Pacifism are no longer counted as stax</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Draw
                                        <ul>
                                            <li>
                                                Reveal to hand effects like Dark Confidant and Yuriko are now counted as
                                                draw
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Evasion
                                        <ul>
                                            <li>Ward effects count as evasion</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Removal
                                        <ul>
                                            <li>
                                                Effects that remove/exile card(s) from graveyards no longer count as
                                                removal
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Cheat
                                        <ul>
                                            <li>Better handling of Cascade effects</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Ramp
                                        <ul>
                                            <li>Better identification of untap effects</li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 02162024.1 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 02.19.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Manuel, Grader of Decks</span>
                                <ul>
                                    <li>
                                        Casual/Precon
                                        <ul>
                                            <li>
                                                Decks are only marked as precon level now if they are power level 0-4
                                                and have a combo score of zero
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Importing</span>
                                <ul>
                                    <li>
                                        TopDecked
                                        <ul>
                                            <li>Fixed import issue for some decks</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Archidekt
                                        <ul>
                                            <li>Fixed issue with parsing categories for cards in decks</li>
                                            <li>Added support for importing decks from articles</li>
                                        </ul>
                                    </li>
                                    <li>
                                        MtGGoldfish
                                        <ul>
                                            <li>Fixed issue with decks that had sideboards</li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 02162024" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 02.16.2024</i>
                                </b>
                            </span>
                            <Text>
                                This one was a bit of sad release.
                                <p />
                                Long story short, I have learned a lot over the course of this project, and the way that
                                I structured the data for decks on the site has changed significantly. This resulted in
                                my having to write a lot of code to transform decks with older data formats to the
                                latest greatest format. Last Thursday I deployed an update to try and update everything
                                all at once on the site. Unfortunately I received an absolutely mammoth bill from AWS
                                from the following 24 hours, so I pulled the site down until I could find a solution.
                                <p />
                                Unfortunately, the only real solution here was to delete the site's database and start
                                over :(
                                <p />
                                There were ~139,000 decks on the site before this... We'll get back up there eventually
                                ;)
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 01042024.2 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 01.30.2024</i>
                                </b>
                            </span>
                            <Text>
                                This update is primarily focused on backend improvements and database optimizations.
                                <p />
                                Also, we onboarded Manuel to be our resident <b>Edgelord in Chief</b>.
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Backend</span>
                                <ul>
                                    <li>Fixed aggregation of decks by commander and author</li>
                                    <li>Caching improvements</li>
                                    <li>General bug fixes and clean up</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 01042024.1 (incremental)" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 01.11.2024</i>
                                </b>
                            </span>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Importing</span>
                                <ul>
                                    <li>
                                        Capped number of cards allowed in deck lists to 110. The reason its not 100 is
                                        to allow people flexibility while building their lists
                                        <ul>
                                            <li>Apologies to all of you with 268+ cards in your deck… 😂</li>
                                        </ul>
                                    </li>
                                    <li>Fixed imports of colorless decks</li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <p />
                                <ul>
                                    <li>* Fixed aggregation of basic fetch lands</li>
                                    <li>
                                        This should make people’s manafixing score and power level go up slightly if
                                        they run basic fetches
                                    </li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Categories</span>
                                <p />
                                <ul>
                                    <li>
                                        Ramp
                                        <ul>
                                            <li>
                                                Cards that allow additional lands per turn, or put lands in to play are
                                                now correctly counted as ramp
                                            </li>
                                            <li>
                                                Scoring has been updated; it now looks at:
                                                <ul>
                                                    <li>
                                                        Play 1 (or more) additional land per turn
                                                        <ul>
                                                            <li>Additional points awarded if it allows more than 1</li>
                                                        </ul>
                                                    </li>
                                                    <li>Puts in to play tapped/untapped</li>
                                                    <li>Whether or not the land searched for has to be basic</li>
                                                </ul>
                                            </li>
                                            <li>Fixed error with aggregating MDFC/transform cards</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Removal
                                        <ul>
                                            <li>Cards that grant Infect are no longer counted as removal</li>
                                            <li>
                                                Cards that do damage divided among targets are now counted as removal
                                            </li>
                                            <li>
                                                Added check for effects that only affect cards that aren’t of a certain
                                                type
                                            </li>
                                            <li>Added stricter scoring based on mana value</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Draw
                                        <ul>
                                            <li>
                                                Effects that look at the top cards of library and then puts them into
                                                your hand are now counted as card draw
                                            </li>
                                            <li>
                                                Stax effects like Spirit of the Labyrinth are no longer counted as card
                                                draw
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Stax
                                        <ul>
                                            <li>
                                                Effects that restrict attacks or blocks (such as Pramikon, Sky Rampart)
                                                are now counted as stax
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Evasion
                                        <ul>
                                            <li>Cards like Silence and Aurelia’s Fury are now counted as evasion</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Tutor
                                        <ul>
                                            <li>
                                                Fixed issue where cards with ‘search’ in their name were incorrectly
                                                counted as tutors
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                                <p />
                                <ul>
                                    <li>Fixed trigger condition checks when there is no card type</li>
                                    <ul>
                                        <li>Example being Rielle’s discard trigger</li>
                                    </ul>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Card Scoring</span>
                                <p />
                                <ul>
                                    <li>
                                        Fixed issue where it incorrectly evaluated mana value for MDFC/transform cards
                                    </li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                                <p />
                                <ul>
                                    <li>Casual</li>
                                    <ul>
                                        <li>Casual decks now get a slight bump in score based on ramp</li>
                                    </ul>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 01042024" isOpen={false}>
                            <span>
                                <b>
                                    <i>Deployed on: 01.04.2024</i>
                                </b>
                            </span>
                            <Text>
                                This update brings a lot of changes for manabase, card, and power level scoring. The
                                focus on all of this is to more accurately suss out a deck's power level. Many decks are
                                going to go down a bit in power level because of this update. Most of this will be due
                                to the fact that the decks in question were scored too highly in the first place.
                                <p />
                                As annoying as this is,{' '}
                                <b>
                                    I would much rather have a deck be scored lower than it objectively is than higher
                                </b>
                                . The reason being that if the site scores your deck as an 8 when it is in reality much
                                lower, and you sit down to play in a high powered pod, you are going to get stomped...
                                And that feels bad.
                                {/*<p />I feel that the vast majority of actual commander decks out there are far below the standards*/}
                                {/*used for cEDH decks... And that is completely fine! Everyone wants a high score, of course, but in*/}
                                {/*terms of actual playability with a group of randos, lower scores tend to be more fun. And that,*/}
                                {/*after all, is the point of not only the format. In short -{' '}*/}
                                {/*<b>do not forget to have the rule zero conversation.</b>*/}
                                {/*<p />*/}
                                {/*All that being said... The power levels generated by this site are not intended to begospel, and*/}
                                {/*they are an ever changing work in progress 😊. If you feel your scores are too low (or too high!),*/}
                                {/*please drop by the discord and let me know!*/}
                            </Text>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Card scores</span>
                                <p />
                                Card scoring is now much more granular. Scoring is derived by looking at the following
                                list:
                                <p />
                                <ul>
                                    <li>
                                        <li>
                                            Casting costs
                                            <ul>
                                                <li>Card CMC</li>
                                                <li>Colored pip density</li>
                                                <li>Alternative costs</li>
                                                <li>
                                                    Additional costs
                                                    <ul>
                                                        <li>Discard</li>
                                                        <li>Sacrifice</li>
                                                        <li>Exile</li>
                                                        <li>Pay life</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </li>
                                        <li>
                                            Activation costs
                                            <ul>
                                                <li>
                                                    This follows the same list as Casting Costs above, in addition to:
                                                    <ul>
                                                        <li>Tap/untap self</li>
                                                        <li>Tap/untap other</li>
                                                        <li>Sacrifice self</li>
                                                        <li>Sacrifice other</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </li>
                                        <li>Ability type (trigger/static/etc)</li>
                                    </li>
                                    <li>
                                        Additionally, there is a second layer of rules logic to determine points for
                                        each category that is used in Power Level calculations.
                                        <ul>
                                            <li>
                                                This logic varies per category, but an example would be exile vs destroy
                                                for removal cards.
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Manabase</span>
                                <p />
                                The scoring for a deck's manabase has been updated/refined in the following ways:
                                <ul>
                                    <li>
                                        Mana Production (per card)
                                        <ul>
                                            <li>Is the amount of mana easily varied (eg Gaea's Cradle)?</li>
                                            <li>Is the amount of mana variable, but not as easily?</li>
                                            <li>How much mana is produced?</li>
                                            <li>
                                                Which colors are produced?
                                                <ul>
                                                    <li>
                                                        This is weighted by the pip density in the deck for the colors
                                                        produced
                                                    </li>
                                                </ul>
                                            </li>
                                            <li>Is it your commander?</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Manabase quality (per card)
                                        <ul>
                                            <li>Does it enter tapped?</li>
                                            <li>
                                                Does it enter tapped unless some condition is met (eg Cinder Glade)?
                                            </li>
                                            <li>Does it need to tap to activate?</li>
                                            <li>Is it available pregame (eg Gemstone Caverns)?</li>
                                            <li>
                                                Can you exile from hand instead of casting (eg Simian Spirit Guide)?
                                            </li>
                                            <li>Does activation require targeting something (eg Deathrite Shaman)?</li>
                                            <li>Does it have conditions for untapping (eg Basalt Monolith)?</li>
                                            <li>Is it fetchable?</li>
                                            <li>How much mana is produced relative to the cmc of the card?</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Mana Fixing (in terms of entire deck)
                                        <ul>
                                            <li>How many fetch lands?</li>
                                            <li>How many lands are fetchable?</li>
                                            <li>
                                                Which colors are produced?
                                                <ul>
                                                    <li>
                                                        This is weighted by the pip density in the deck for the colors
                                                        produced
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                                <ul>
                                    <li>
                                        Combos
                                        <ul>
                                            <li>
                                                The scoring for combos has evolved quite a bit. We now look at:
                                                <ul>
                                                    <li>How many tutors are available for the cards in the combo</li>
                                                    <li>Castability of cards in combo</li>
                                                    <li>Number of cards in combo</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Baseline thresholds
                                        <ul>
                                            <li>
                                                The baseline thresholds for consistency, interaction, and efficiency
                                                have been adjusted based on the output from the new card scoring system.
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Power Level requirements
                                        <ul>
                                            <li>
                                                Several baseline requirements have been added to gatekeep higher power
                                                levels. The thinking is that realistically 7 and 8 are decks that are on
                                                their way to being cEDH, so they need to be oriented more towards a
                                                competitive play style.
                                                <ul>
                                                    <li>
                                                        Power Level 7+
                                                        <ul>
                                                            <li>Must include some form of combo</li>
                                                            <li>
                                                                Must have a manafixing score greater than or equal to 80
                                                            </li>
                                                        </ul>
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>Card Categories</span>
                                <p />
                                Detection of the following card categories has been improved:
                                <ul>
                                    <li>Removal</li>
                                    <li>Draw</li>
                                    <li>Ramp</li>
                                    <li>Recursion</li>
                                    <li>Groupslug</li>
                                    <li>Stax</li>
                                    <li>Taxes</li>
                                </ul>
                                <div style={{ height: 30 }} />
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 11022023.7 (incremental)" isOpen={false}>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>cEDH Meta</span>
                                <ul>
                                    <li>Meta updated</li>
                                    <li>Staples list updated</li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Discord</span>
                                <p />
                                <ul>
                                    <li>
                                        The "/check" command now returns a summary image
                                        <ul>
                                            <li>
                                                <img src={'/resources/discord_banner_preview.png'} />
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 11022023.6 (incremental)" isOpen={false}>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Deck Importing</span>
                                <p />
                                <ul>
                                    <li>
                                        Archidekt
                                        <ul>
                                            <li>Fixed issue wherein sideboards were included in card aggregation</li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 11022023.5 (incremental)" isOpen={false}>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                                <p />
                                <ul>
                                    <li>
                                        Interaction
                                        <ul>
                                            <li>Taxes are now included in the calculations for interaction</li>
                                            <li>
                                                Interaction piece scores are now weighted by whether or not they are
                                                'slow'
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>UI</span>
                                <ul>
                                    <li>
                                        Power Levels
                                        <ul>
                                            <li>Now displaying Tax effects in list of cards for power level scoring</li>
                                            <li>
                                                Standardizing columns/fields in list shown between cEDH and Casual views
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 11022023.4 (incremental)" isOpen={false}>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Categories</span>
                                <p />
                                <Text>Detection algorithms for the following categories have been improved:</Text>
                                <ul>
                                    <li>Cheat</li>
                                    <li>Counterspells</li>
                                    <li>Evasion</li>
                                    <li>Ramp</li>
                                    <li>Removal</li>
                                    <li>Stax</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 11022023.3 (incremental)" isOpen={false}>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Categories</span>
                                <p />
                                <ul>
                                    <li>Ramp - mana rocks are once again being listed</li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Deck importing</span>
                                <p />
                                <ul>
                                    <li>mtggoldfish.com - fixed import error with decks from this site</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 11022023.2 (incremental)" isOpen={false}>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Categories</span>
                                <p />
                                <Text>Detection algorithms for the following categories have been improved:</Text>
                                <ul>
                                    <li>Draw</li>
                                    <li>Evasion</li>
                                    <li>Fastmana</li>
                                    <li>Grouphug</li>
                                    <li>Groupslug</li>
                                    <li>Ramp</li>
                                    <li>Removal</li>
                                    <li>Stax</li>
                                    <li>Tutor</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 11022023.1 (incremental)" isOpen={false}>
                            <Text>
                                <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                                <ul>
                                    <li>Capped 'stax weighting' bonuses</li>
                                </ul>
                            </Text>
                            <Text>
                                The stax weighting bonus is meant to bolster deck scores that are more reliant on stax
                                than combo to win. This was necessary in earlier versions of the app because combo was
                                weighted more heavily. However, the scoring algorithm appears to be more accurate now,
                                and Winota decks were getting higher scores than their meta share would dictate.
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 11022023.0" isOpen={false}>
                            <Text>
                                <p />
                                <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                                <ul>
                                    <li>
                                        Massive reworking of synergy lookups. The following should be more correct:
                                        <ul>
                                            <li>Card type matches</li>
                                            <li>Enablers</li>
                                            <li>Triggers found</li>
                                        </ul>
                                    </li>
                                </ul>
                            </Text>
                            <Text>
                                It is possible that I broke some synergies that I am not aware of. If you spot
                                something, feel free to let me know in the discord!
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 10072023.1 (incremental)" isOpen={false}>
                            <Text>
                                <p />
                                <span style={{ fontWeight: 'bolder' }}>Synergy</span>
                                <ul>
                                    <li>
                                        Cost reduction synergy has been added
                                        <ul>
                                            <li>Checks color, permanent type, etc</li>
                                            <li>Only includes cards that could actually be reduced</li>
                                            <ul>
                                                <li>eg Sapphire Medallion does not reduce Mystic Remora</li>
                                            </ul>
                                        </ul>
                                    </li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Manabase Analysis</span>
                                <ul>
                                    <li>Added bonus and cmc adjustments for cost reduction effects</li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Card Categories</span>
                                <br />
                                <ul>
                                    <li>
                                        Cheat
                                        <ul>
                                            <li>
                                                Rules expanded to include cascade as well as effects like Etali, Primal
                                                Conqueror, Doors of Durin, Vorinclex // The Grand Evolution
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>UI</span>
                                <ul>
                                    <li>Added cost reduction information to Manabase tab.</li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                        <ChangelogPane title="Platform Release: 10072023.0" isOpen={false}>
                            <Text>
                                This update is primarily focused on correcting decks whose Power Levels were rated
                                higher than they should be.
                                <p />
                                <span style={{ fontWeight: 'bolder' }}>Manabase Analysis</span>
                                <br />
                                This new set of rules look at the following criteria when reading a deck:
                                <ul>
                                    <li>Base land quantity</li>
                                    <li>Total mana production relative to the average CMC of nonland cards</li>
                                    <li>
                                        Ratio of colored mana production relative to pip density in cards for each color
                                        in the deck
                                    </li>
                                    <li>Presence of fetches</li>
                                    <li>Whether or not the land/rock/dork ETBs tapped</li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Card Categories</span>
                                <br />
                                The following categories have been added:
                                <ul>
                                    <li>
                                        Pregame
                                        <ul>
                                            <li>
                                                This covers effects like Leyline of Anticipation, Gemstone Caverrns, etc
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                And these have been updated and improved:
                                <ul>
                                    <li>
                                        Slow
                                        <ul>
                                            <li>Detection of ETB Tapped and such should be better now</li>
                                        </ul>
                                    </li>
                                    <li>
                                        Cheat
                                        <ul>
                                            <li>
                                                Better detection of effects that allow one to cheat cards in to play.
                                                Cards like Tooth and Nail should now be accounted for
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <div style={{ height: 30 }} />
                                <span style={{ fontWeight: 'bolder' }}>Power Levels</span>
                                <br />
                                This has been tweaked in the following ways:
                                <ul>
                                    <li>
                                        Manabase score is now much more heavily weighted when generating a power level
                                        score
                                    </li>
                                    <li>
                                        Decks that have less than 10 mana-producing lands are now automatically a 1.1.
                                        <ul>
                                            <li>
                                                There are a lot of decks on the site currently that tried to get the
                                                highest power level and salt level possible by making unplayable decks
                                            </li>
                                            <li>
                                                The baseline amount of 10 could be too severe - I will increase it if I
                                                get feedback requesting it
                                            </li>
                                            <li>
                                                This override score supersedes any power level that would have been
                                                otherwise generated
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        Decks that have a power level rating less than 5 get a boost based on their
                                        synergy score
                                        <ul>
                                            <li>
                                                The synergy tool is very much still a work in progress, and I am leery
                                                of rushing and having it affect all decks all at once.
                                            </li>
                                            <li>
                                                When looking at the deck scores, it seems that a lot of the decks that
                                                are under 5 are more synergy focused
                                            </li>
                                            <li>
                                                Additionally, most of the cEDH lists I looked at appeared to have much
                                                lower synergy scores, which surprised me at first. However it does seem
                                                to make sense - those decks often tend to be "value piles" aimed at
                                                being as efficient as possible.
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <span style={{ fontWeight: 'bolder' }}>UI</span>
                                <br />
                                The website UI has been updated:
                                <ul>
                                    <li>
                                        New Manabase Analysis section and score. This UI is still early in the
                                        development stage and will be incrementally updated
                                    </li>
                                    <li>
                                        Lists of individual card scores for Landbase and Ramp have been removed from the
                                        Power Levels tab. Please refer to the new Manabase tab for this information
                                    </li>
                                </ul>
                            </Text>
                        </ChangelogPane>
                    </>
                </div>
            </div>
        </Flex>
    );
}
